import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    supportedLngs: ['en', 'fr', 'es'],
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    resources: {
      en: {
        translation: {
          tabs: { home: 'Home', progress: 'Progress', exams: 'Exams', timetable: 'Timetable', more: 'More' },
          settings: { title: 'App Settings', language: 'Language', app_language: 'Application language' },
          actions: {
            signout: 'Sign out', copy: 'Copy Code', previous: 'Previous', next: 'Next',
            cancel: 'Cancel', saving: 'Saving…', generating: 'Generating…', copied: '✓ Copied!',
            delete: 'Delete', remove: 'Remove', filter_authorities: 'Filter authorities…',
            loading: 'Loading…', creating: 'Creating…',
          },
          parent_dash: {
            title: 'Family Overview',
            empty: 'No upcoming exams or active proposals',
            add_hs: 'Add a child',
            link_student: 'Link a student',
            link_account: 'Link Account',
            invite_code: 'Invite code',
            regen_code: 'Regenerate Join Code',
            regen_new_code: 'New code',
            regen_failed: 'Failed to regenerate. Please try again.',
            next_exam: 'Next Exam',
            ai_proposal: 'AI Proposal',
            student_fallback: 'Student',
            no_students_linked: "You haven't linked any student profiles yet. Add a High School student or link an existing one below.",
            link_success: 'Student linked successfully!',
            link_err_expired: 'This invite code has expired. Ask your student to generate a new one.',
            link_err_invalid: 'Invalid invite code. Please check and try again.',
            link_err_generic: 'Something went wrong. Please try again.',
          },
          parent_student: {
            profile_title: 'Student Profile',
            country: 'Country',
            authority: 'Authority',
            progress: 'Study Progress',
            flashcards: 'Flashcards Flipped',
            quiz_avg: 'Quiz Average',
            topics: 'Explored Topics',
            homework_completed: 'Completed',
            weekly: 'Weekly Study',
            weekly_schedule: 'Weekly Schedule',
            today_proposals: "Today's study proposals",
            age_label: '{{age}} years old',
            schedule_desc: 'View full class schedule and extracurricular activities.',
            view_schedule: 'View Full Schedule',
            settings_profile: 'Settings & Profile',
            role_parent: 'Parent',
            lang: 'Language',
            sign_out: 'Sign Out',
            edit_profile: 'Edit Profile',
            select_country: 'Select country',
            no_authority_match: 'No authorities match your search.',
            no_topics: 'No topics explored yet.',
            no_proposals: "No proposals yet. Make sure your child has set up their timetable and confidence ratings.",
            slot_label: 'Slot: {{slot}}',
            no_homework: 'No homework added yet.',
            open_label: 'Open',
            homework_aria: 'Homework {{n}}',
            save: '💾 Save',
            no_schedule_entries: 'No schedule entries yet.',
            classes_label: 'Classes',
            free_time_disclaimer: 'Manually declared by the student — for reference only, not used to schedule AI study proposals.',
            update_schedule: 'Update timetable',
            upload_schedule: 'Upload timetable',
          },
          academic_context: {
            study_level: 'Level of Study',
            select_study_level: 'Select level of study',
            confirm_title: 'Reset academic year?',
            confirm_body: 'Changing your grade or country will clear your current timetable, free time, and commitments. You will need to upload your new schedule immediately. Continue?',
            confirm_continue: 'Continue',
            banner_title: 'New academic year set — please upload your timetable',
            banner_desc: 'Your timetable, free time, and commitments were cleared for the new academic year. Upload your new schedule to get study proposals again.',
          },
          parent_subscription: {
            title: 'Subscription',
            no_children: 'Add a child to see pricing for them.',
            add_child_cta: 'Add a child',
            select_child: 'Child',
          },
          student_subscription: {
            title: 'Subscription',
          },
          subscription: {
            tier_accessible: 'Accessible',
            tier_standard: 'Standard',
            tier_premium: 'Premium',
            monthly: 'Monthly',
            yearly: 'Annual (one-off payment)',
            per_month: '/month',
            per_year: '/year',
            per_child_note: 'Price shown is per child.',
            sessions: '{{count}} sessions/month',
            unlimited_sessions: 'Unlimited sessions',
            loading: 'Loading pricing…',
            error: "Couldn't load pricing. Please try again.",
          },
          parent_child_detail: {
            child_fallback: 'Child',
            university_note: 'University students manage their own study data. You can see weekly totals and streak only.',
            tab_overview: 'Overview',
            tab_timetable: 'Timetable',
            tab_free_time: 'Free time',
            tab_confidence: 'Confidence',
            tab_study_plan: 'Study plan',
            tab_progress: 'Progress',
          },
          overview_tab: {
            signin_code_title: 'Student sign-in code',
            share_code_desc: "Share this 8-digit code with your child — valid until {{date}}.",
            copy: 'Copy',
            generate_new_code: 'Generate new code',
            generate_code_desc: 'Generate an 8-digit code for your child to sign in for the first time.',
            generate_code: 'Generate code',
            ai_suggestion_title: 'AI Study Suggestion',
            generating_suggestion: 'Generating suggestion…',
            suggestion_failed: 'Could not generate suggestion. Check that timetable and confidence are set up.',
            generated_at: 'Generated {{time}}',
            loading_proposals: 'Loading proposals…',
            no_proposals: 'No proposals yet. Set up confidence ratings and free time slots first.',
          },
          timetable_tab: {
            class_count: '{{count}} classes',
            grid_view: 'Grid',
            edit_view: 'Edit',
            subject_label: 'Subject',
            day_label: 'Day',
            start_time_label: 'Start time',
            end_time_label: 'End time',
            room_label: 'Room (optional)',
            remove: 'Remove',
            add_class: '+ Add class',
            save_failed: 'Failed to save timetable',
            saved: 'Timetable saved.',
            save_timetable: 'Save timetable',
            free_time_heading: 'Free time',
          },
          free_time_tab: {
            intro_desc: "This is your own reference list of extra free blocks — it's not used to generate AI study proposals. Study proposals are automatically scheduled around your timetable and activities.",
            label_label: 'Label (optional)',
            label_placeholder: 'e.g. After school',
            save_failed: 'Failed to save free time',
            add_slot: '+ Add slot',
            saved: 'Free time saved.',
            save_free_time: 'Save free time',
          },
          confidence_tab: {
            intro_desc: 'Rate confidence for each subject. The AI uses this to prioritise study proposals.',
            no_subjects: 'No subjects yet. Add timetable entries or type a subject below.',
            add_subject_placeholder: 'Add a subject manually…',
            add_btn: 'Add',
            save_failed: 'Failed to save confidence ratings',
            saved: 'Confidence saved.',
            save_btn: 'Save confidence ratings',
          },
          progress_tab: {
            title: 'Study sessions',
            cancel: 'Cancel',
            log_session: '+ Log session',
            log_session_title: 'Log a study session',
            subject_label: 'Subject',
            select_subject: 'Select subject…',
            subject_placeholder: 'e.g. Maths',
            topic_label: 'Topic',
            topic_placeholder: 'e.g. Quadratic equations',
            format_label: 'Format',
            format_short: 'Short (15 min)',
            format_standard: 'Standard (30 min)',
            format_deep: 'Deep (60 min)',
            duration_label: 'Duration (minutes)',
            note_label: 'Note (optional)',
            note_placeholder: 'What did you cover?',
            log_btn: 'Log session',
            logged: 'Session logged!',
            no_sessions: 'No sessions logged yet.',
            log_first: 'Log your first session',
            log_failed: 'Failed to log session. Please try again.',
          },
          proposals_tab: {
            confidence_heading: 'Subject confidence ratings',
            no_ratings: "No ratings yet. Ask your child to rate their subjects from their home page.",
            plan_heading: "Today's study plan",
            plan_desc: "Generated from your child's timetable, confidence ratings, and study history.",
            no_proposals: 'No proposals yet.',
            no_proposals_desc: 'Proposals are generated once your child has a timetable set up.',
            rated_label: 'Rated:',
            suggested_slot: 'Suggested slot: {{slot}}',
          },
          child_context_edit: {
            authority_required: 'Please select at least one education authority.',
            update_failed: 'Update failed',
            back: 'Back',
            title: 'Edit education context',
            desc: "Update {{name}}'s country, authority, and class.",
            desc_fallback: 'your child',
            country_label: 'Country of study',
            authorities_label: 'Education authorities',
            select_all_hint: '(select all that apply)',
            class_year_label: 'Class / Year',
            save_changes: 'Save changes',
          },
          child_context_card: {
            title: 'Education context',
            edit_link: 'Edit →',
            curriculum_label: 'Curriculum',
            official_loaded: '✓ Official data loaded',
            data_pending: 'Data pending',
            official_subjects_prefix: 'Official subjects: {{subjects}}',
            more_suffix: '+{{count}} more',
          },
          uni_more: {
            greeting: 'Hi, {{name}}',
            student_fallback: 'Student',
            ai_tools_label: 'AI tools',
            flashcards: 'Flashcards',
            my_schedule_label: 'My schedule',
            my_timetable: 'My timetable',
            commitments: 'Commitments',
            invite_parent_title: 'Invite a Parent',
            invite_parent_desc: 'Grant a parent summary access to your study streak. They will not see your notes.',
          },
          scan_timetable: {
            title: 'Scan Schedule',
            read_error: 'Could not read the image. Please try again.',
            ocr_failed: 'Claude could not read the photo. Try a clearer image, or enter your timetable manually.',
            scanning_title: 'Claude is reading your schedule…',
            scanning_desc: 'This usually takes a few seconds.',
            scan_camera_title: 'Scan paper timetable',
            scan_camera_desc: 'Point your camera at your paper schedule — Claude reads it automatically.',
            upload_title: 'Upload a photo',
            upload_desc: 'Choose an existing photo from your gallery or files.',
            or_divider: '— or —',
            enter_manually: '✍️ Enter manually instead',
          },
          notification_bell: {
            disable: 'Disable study reminders',
            blocked: 'Notifications blocked in browser settings',
            enable: 'Enable daily study reminders',
          },
          streak_pill: {
            aria: '{{count}}-day streak',
            days_one: '{{count}} day',
            days_other: '{{count}} days',
          },
          countdown_chip: {
            today: 'Today!',
            days_left_one: '{{count}} day left',
            days_left_other: '{{count}} days left',
            with_label: '{{label}}: {{text}}',
          },
          weekly_grid: {
            legend_school: 'School',
            legend_activity: 'Activity',
            legend_free: 'Free time',
            time_col: 'Time',
            aria_label: 'Weekly timetable',
            free_fallback: 'Free',
          },
          error_boundary: {
            title: 'Something went wrong',
            desc: "An unexpected error occurred. We've been notified.",
            go_home: 'Go home',
          },
          app_shell: {
            skip_to_content: 'Skip to content',
            greeting: 'Hi, {{name}}!',
          },
          not_found: {
            title: 'Page not found',
            desc: "This page doesn't exist or was moved.",
          },
          offline: {
            title: "You're offline",
            desc: 'VADOStudy needs a connection to generate content. Cached pages are still available.',
            retry: 'Retry connection',
          },
          network_status: {
            offline_banner: 'You are offline. Some features may be unavailable.',
          },
          install_prompt: {
            banner_text: 'Install VADOStudy for quick access, even offline.',
            install_btn: 'Install',
            ios_install_btn: 'How to install',
            dismiss: 'Dismiss',
            ios_sheet_title: 'Install VADOStudy',
            ios_step_1: 'Tap the Share icon in Safari’s toolbar.',
            ios_step_2: 'Scroll down and select “Add to Home Screen.”',
            ios_step_3: 'Tap “Add” — VADOStudy will appear on your Home Screen.',
            ios_got_it: 'Got it',
          },
          link_student: {
            err_length: 'Please enter the full 8-character invite code.',
            success: '{{name}} has been linked to your account.',
            err_not_found: 'Invite code not found. Check the code and try again.',
            err_generic: 'Failed to link student. Please try again.',
            dashboard_btn: 'Dashboard',
            title: 'Link a student',
            desc: 'Enter the 8-character invite code your university student shared with you.',
            submit_btn: 'Link student',
            hint_prefix: 'Ask your student to go to',
            hint_path: 'More → Invite a parent',
            hint_suffix: 'in the VADOStudy app to generate their code.',
          },
          parent_footer: { family: 'Family', sub: 'Subscription', more: 'More' },
          student_dash: {
            greeting: 'Hi {{name}}',
            up_next: 'Up next',
            day_schedule: '{{day}} schedule',
            explain: 'Explain',
            homework: 'Homework',
            no_open_assignments: 'No open assignments',
            cards_btn: 'Cards',
            add_exam_cta: 'Add your next control dates',
            degraded_notice: 'Showing basic suggestions — AI temporarily unavailable',
            upcoming_exam: 'Upcoming Exam',
            no_timetable_title: 'No timetable yet!',
            no_timetable_desc: 'Claude needs to know your class schedule to generate your personalised study plan.',
            setup_timetable: 'Set up my timetable',
            load_failed: 'Failed to load dashboard data.',
            no_proposals: 'No study proposals yet. Rate your subjects so Claude can prioritise your plan.',
            rate_subjects: 'Rate my subjects',
            get_homework_help: 'Get homework help',
            my_homework: 'My Homework',
            no_classes_today: 'No classes scheduled for today! 🎉',
            hw_done_count: '{{done}}/{{total}} done',
          },
          notifications: {
            study_reminders: 'Study reminders',
            daily_digest_desc: 'Daily digest and inactivity alerts',
          },
          student_progress: {
            title: 'My Progress',
            recent_topics: 'Recently Explored Topics',
            algo_title: 'Update your AI algorithm',
            algo_desc: 'Rate how you are feeling about your classes so Claude can generate better study plans',
            review_btn: 'Review Subject Confidence',
            flashcards_flipped: 'Flashcards Flipped',
            quiz_average: 'Quiz Average',
            homework_completed_heading: 'Homework Completed',
            exercise_count: '{{done}} / {{total}} exercise(s)',
          },
          student_timetable: {
            title: 'My Schedule',
            other: 'Other activities',
            add_free: 'Add Free time',
            edit: 'Edit timetable',
            days: { Mon: 'Mon', Tue: 'Tue', Wed: 'Wed', Thu: 'Thu', Fri: 'Fri', Sat: 'Sat', Sun: 'Sun' },
            days_full: {
              monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
              friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
            },
            empty_title: 'No schedule yet',
            empty_desc: 'Add your classes so Claude can build your personalised study plan.',
            scan_btn: '📸 Scan paper timetable',
            enter_manually: '✍️ Enter manually',
            activities_heading: '⚽ Activities',
            edit_activities: '✏️ Edit activities',
            add_activities: '⚽ Add activities',
            free_time_heading: '⏳ Free Time',
            free_block: 'Free block',
            edit_free_time: '✏️ Edit free time',
            commitment_types: { sport: 'Sport', music: 'Music', work: 'Work', other: 'Other' },
          },
          school_calendar: {
            view_button: '📅 School Calendar',
            title: 'School Calendar',
            trimesters_heading: 'Term Dates',
            holidays_heading: 'Holidays',
            exams_heading: 'National Exam Windows',
            zone_label: 'Zone {{zone}}',
            nationwide: 'Nationwide',
            loading: 'Loading calendar…',
            pending: 'Fetching the latest calendar… this can take a moment.',
            empty: 'The school calendar for your region isn’t available yet. Please check back later.',
            error: 'We couldn’t load the school calendar right now. Please try again later.',
          },
          student_more: {
            settings_profile: 'Settings & Profile',
            country: 'Country',
            authority: 'Authority',
            study_again: 'Study again',
            subject: 'Subject',
            select_subject: 'Select subject',
            theme: 'Topic/Theme',
            explain_this: 'Explain this',
            new_flashcards: 'New Flashcards',
            notif_parent: 'Notifications for your account are managed by your parent.',
            revision_plan: 'Revision plan',
            revision_plan_desc: 'Full week of study proposals',
            default_name: 'Student',
            edit_details: 'Edit details',
            select_country: 'Select your country',
            save_changes: '💾 Save changes',
            subject_placeholder: 'e.g. Mathematics',
            theme_placeholder: 'e.g. Quadratic equations, WW2, Photosynthesis…',
            family_privacy: 'Family Privacy',
            invite_desc: 'Generate a secure code to let a parent view your study streak.',
            generate_invite: 'Generate Invite Code',
            valid_until: 'Valid until {{date}}',
            generate_new_code: 'Generate new code',
            push_reminders_desc: 'Receive daily push reminders on this device.',
            disable_notifications: 'Disable notifications',
            enable_notifications: 'Enable notifications',
          },
          revision_plan: {
            empty_title: 'No proposals yet.',
            empty_desc: 'Add your timetable and exams to get personalised suggestions.',
            scheduled: 'Scheduled: {{slot}}',
          },
          manage_commitments: {
            title: 'Other Activities',
            add_activity: 'Add Activity',
            name_label: 'Name *',
            name_placeholder: 'e.g. Football training',
            type_label: 'Type',
            day_label: 'Day',
            start_time_label: 'Start time',
            end_time_label: 'End time',
            save_activity: '➕ Save Activity',
            current_activities: 'Current Activities',
            empty: 'No activities yet. Add one above.',
            delete_aria: 'Delete {{label}}',
            added_toast: 'Activity added!',
            add_failed_toast: 'Failed to add activity. Try again.',
            removed_toast: 'Activity removed.',
            delete_failed_toast: 'Failed to delete. Try again.',
          },
          manage_free_time: {
            title: 'Add Free Time',
            add_block: 'Add a Block',
            label_optional: 'Label (optional)',
            label_placeholder: 'e.g. After school, Weekend morning',
            add_to_list: '➕ Add to list',
            slots_heading: 'Free Time Slots ({{count}})',
            empty: 'No blocks added yet.',
            remove_slot_aria: 'Remove slot',
            save_btn: '💾 Save Free Time',
            saved_toast: 'Free time saved!',
            save_failed_toast: 'Save failed. Please try again.',
          },
          manage_timetable: {
            edit_title: 'Edit Timetable',
            preview_heading: 'Schedule Preview',
            scan_read_error: 'Scan failed — could not read the image. Try again or add classes manually.',
            scan_unclear_error: 'Scan failed — photo unclear? Try again or add classes manually.',
            scan_success: 'Claude detected {{count}} class(es). Review below and save when ready.',
            scanning: '⏳ Scanning with Claude…',
            scan_photo_btn: '📷 Scan timetable photo',
            or_manual: 'or add manually',
            add_class: 'Add a Class',
            subject_label: 'Subject *',
            room_label: 'Room (optional)',
            room_placeholder: 'e.g. A12',
            classes_heading: 'Classes ({{count}})',
            empty: 'No classes added yet.',
            remove_entry_aria: 'Remove entry',
            save_btn: '💾 Save Timetable',
            saved_toast: 'Timetable saved!',
          },
          confidence: {
            title: 'Subject Confidence',
            desc: 'Rate how you feel about your classes to get better AI study proposals.',
            struggling: 'Struggling',
            on_track: 'On Track',
            confident: 'Confident',
            no_subjects: 'No subjects found. Your timetable may still be loading.',
          },
          ai_session: {
            offline_title: 'No internet connection',
            offline_cta: 'Connect to internet to use AI tools',
            limit_reached: 'You have reached your monthly AI session limit.',
            back_to_dashboard: 'Back to Dashboard',
          },
          explain: {
            leave: '← Leave Session',
            got_it: 'Got it! (+1 Streak)',
            deep_dive: 'Deep Dive',
            simpler: 'Explain Simpler',
            claude_typing: 'Claude is typing…',
            prompt_question: 'What should Claude explain for you?',
            topic_theme: 'Topic / Theme',
            topic_placeholder: 'e.g. Pythagorean theorem, World War 2, Photosynthesis…',
            ask_btn: '💬 Ask Claude to explain',
            generate_failed: 'Failed to get explanation',
            deep_dive_msg: 'I want a deep dive into this — more detail and examples please.',
            simpler_msg: 'Can you explain this more simply?',
          },
          quiz: {
            title: 'Quiz Challenge',
            choose: 'Choose how many questions you want to tackle:',
            questions: '{{count}} Questions',
            header_title: 'Quiz',
            generating: 'Claude is composing your custom quiz items…',
            generate_failed: 'Failed to generate quiz',
            results_title: 'Results',
            complete_title: 'Quiz Complete!',
            your_score: 'Your Score',
            pct_correct: '{{pct}}% correct',
            result_excellent: 'Excellent work!',
            result_good: 'Good effort!',
            result_keep_practising: 'Keep practising!',
            try_another: 'Try another quiz',
            running_score: 'Score: {{score}} / {{total}}',
          },
          flashcard: {
            question_label: 'Question',
            tap_flip: 'Tap to Flip',
            got_it: 'Got it',
            need_practice: 'Need Practice',
            generate_failed: 'Failed to generate flashcards',
            generating_session: 'Claude is generating your personalized study session…',
            choose_subject: 'Choose a subject…',
            theme_placeholder: 'e.g., The French Revolution, Trigonometry…',
            answer_label: 'Answer',
            empty_state: 'Could not generate flashcards. Please try again.',
          },
          session_complete: {
            title: 'Session Complete!',
            streak: '{{count}} Day Streak',
            streak_plus: '+1 to Streak',
            great_work: 'Great work on {{subject}}. Keep your momentum going!',
            momentum: 'Keep your momentum going!',
            subject: 'Subject',
            theme: 'Theme',
            start_quiz: 'Start Quiz',
            explain_this: 'Explain This',
            new_cards: 'New Cards',
            back_dash: 'Back to Dashboard',
          },
          exams: {
            title: 'Exams',
            add_exam_btn: '+ Add your next control dates',
            no_exams: 'No exams yet.',
            add_first: 'Add your first exam',
            past_exams: 'Past exams ({{count}})',
            remove: 'Remove',
            subject: 'Subject',
            exam_name: 'Exam name',
            date: 'Date',
            select_subject: 'Select subject…',
            add_error: 'Could not add exam. Please try again.',
            sheet_title: 'Add your next control dates',
            subject_placeholder: 'e.g. Maths',
            name_placeholder: 'e.g. Terminal exam',
            delete_aria: 'Delete exam {{name}}',
          },
          exams_grid: {
            title: 'Upcoming Exams',
            free_label: 'Free',
            editing_label: 'Editing: {{slot}}',
            close: 'Close',
          },
          add_child: {
            title: 'Add Child',
            first_name: 'First Name',
            mode: 'Mode',
            high_school: 'High School',
            university: 'University',
            year_of_study: 'Year of Study',
            select_year: 'Select a year',
            date_of_birth: 'Date of Birth',
            gdpr_note: 'Required for students under 16 (GDPR)',
            education_authority: 'Education Authority',
            search_authority: 'Search authorities…',
            selected: '{{count}} selected',
            track_helper: 'Choose up to {{count}}. These subjects get extra depth and priority in AI suggestions.',
            generate_code: 'Generate Login Code',
            name_placeholder: 'e.g. Emma',
            authority_required: 'Please select at least one education authority.',
            year_required: 'Please select a year of study.',
            year_cleared_notice: 'Year of study was cleared because it isn\'t available for this country — please re-select.',
            dob_age_range: 'Date of birth must indicate an age between 10 and 25.',
            err_generic: 'Something went wrong. Please try again.',
            profile_created: 'Profile Created!',
            share_code_desc: 'Share this code with {{name}}. They must enter it on their device to link their account.',
            valid_until: 'Valid until {{date}}',
            valid_72h: 'Valid for 72 hours',
            shown_once: 'shown only once',
            done_btn: 'Done',
          },
          homework: {
            title: 'Homework',
            ocr_pending: '⏳ Extracting exercises…',
            add_btn: '+ Add Homework',
            add_hw_title: 'Add Homework',
            subject: 'Subject',
            select_subject: 'Select a subject',
            topics: 'Topics / Theme',
            add_theme: '+ Add',
            image_label: 'Homework image',
            image_optional: '(optional — Claude will extract exercises)',
            tap_upload: 'Tap to upload a photo',
            create_btn: 'Create Homework',
            done: 'done',
            exercise: 'Exercise',
            progress_label: 'Progress',
            ai_help: 'Get AI help with this exercise',
            chat_prompt: 'Ask a question about this exercise.',
            chat_guide: 'The AI will guide you — not solve it for you.',
            chat_placeholder: 'Ask for a hint…',
            chat_send: 'Send',
            empty: 'No homework yet. Tap "Add Homework" to get started.',
            open_section: 'Open',
            completed_section: 'Completed',
            done_badge: 'Done',
            delete_aria: 'Delete {{subject}}',
            item_aria: 'Homework {{index}}',
            not_found: 'Homework not found.',
            back_to_list: 'Back to homework list',
            view_document: '📄 View original document',
            document_alt: 'Homework document',
            help_title: 'Homework help',
            loading_exercise: 'Loading exercise…',
            see_document: '📄 See document',
            chat_error: 'Could not get a response. Please try again.',
            review_title: 'Review Exercises',
            extracted_count: '{{count}} exercise(s) extracted by Claude',
            start_working: '🚀 Start working',
            other_option: 'Other…',
            subject_placeholder: 'e.g. Mathematics',
            enter_subject_name: 'Enter subject name',
            topic_placeholder: 'e.g. Differentiation',
            scan_btn: '📷 Scan',
            upload_btn: '📁 Upload',
            supported_formats: 'Supported formats: JPEG, PNG, WebP, PDF.',
            subject_required: 'Please select or enter a subject.',
            create_failed: 'Could not create homework. Please try again.',
            preview_alt: 'Preview',
          },
          auth: {
            forgot_password: 'Forgot password?',
            forgot_title: 'Reset your password',
            forgot_desc: "Enter your email and we'll send a reset code.",
            send_code: 'Send reset code',
            check_email_title: 'Check your email',
            check_email_desc: 'We sent a 6-digit code to {{email}}. Enter it below.',
            code_label: 'Verification code',
            new_password: 'New password',
            confirm_password: 'Confirm password',
            confirm_reset: 'Set new password',
            passwords_mismatch: 'Passwords do not match',
            resend_code: 'Resend code',
            back_to_login: 'Back to sign in',
            success_banner: 'Password updated — please sign in.',
            err_not_found: 'No account found for this email.',
            err_code_mismatch: 'Invalid or expired code. Please try again.',
            err_weak_password: 'Password must be at least 8 characters.',
            child_welcome: 'Welcome, Student!',
            child_instructions: 'Enter the 8-digit code provided by your parent to access your study space.',
            verifying: 'Verifying...',
            sign_in_btn: 'Sign In',
            join_code_invalid: 'Invalid or expired join code. Ask your parent to generate a new one.',
            tagline: 'AI-powered study organiser',
            language_label: 'Choose your language',
            sign_in_failed: 'Sign in failed. Please try again.',
            email_label: 'Email',
            email_placeholder: 'you@example.com',
            password_label: 'Password',
            signing_in: 'Signing in…',
            no_account: "Don't have an account?",
            create_one: 'Create one',
            have_join_code: 'Student with a join code?',
            sign_in_here: 'Sign in here',
            university_prompt: 'University student?',
            create_account_btn: 'Create your account',
          },
          register: {
            parent_tagline: 'Create your parent account',
            create_account_heading: 'Create account',
            password_hint: 'Minimum 12 characters · uppercase, lowercase, number and symbol required',
            continue_btn: 'Continue',
            already_have_account: 'Already have an account?',
            sign_in_link: 'Sign in',
            verify_email_heading: 'Verify your email',
            code_sent: 'We sent a 6-digit code to',
            confirmation_code_label: 'Confirmation code',
            verify_btn: 'Verify',
            your_profile_heading: 'Your profile',
            first_name_label: 'Your first name',
            privacy_choices: 'Privacy choices',
            consent_ai_label: 'Allow AI-powered study suggestions (uses Claude API)',
            consent_curriculum_label: 'Allow curriculum data enrichment (French national programme)',
            tos_prefix: 'By continuing you accept the',
            tos_terms: 'VADOStudy Terms of Service',
            tos_suffix: 'and confirm all data is processed in {{region}}.',
            region_eu_west_3: 'the EU (Paris region)',
            region_af_south_1: 'Africa (Cape Town region)',
            get_started_btn: 'Get started',
            signup_failed: 'Sign up failed',
            confirm_failed: 'Confirmation failed',
            profile_failed: 'Profile creation failed',
          },
          register_parent: {
            err_password: 'Password must be at least 8 characters and include uppercase, lowercase, a number, and a symbol.',
            err_exists: 'An account with this email already exists. Try signing in.',
            err_code_mismatch: 'Incorrect code. Please check your email and try again.',
            err_code_expired: 'Code expired. Please go back and request a new one.',
            err_generic: 'Something went wrong. Please try again.',
            code_resent: 'A new code has been sent to your email.',
            resending: 'Resending…',
            title: 'Create Parent Account',
            step1_desc: "Monitor your child's learning progress and AI study plans.",
            email_address_label: 'Email Address',
            password_placeholder: 'At least 8 characters',
            creating_account: 'Creating account…',
            create_account_btn: 'Create Account',
            check_email_heading: 'Check your email',
            verifying: 'Verifying…',
            verify_continue_btn: 'Verify & Continue',
            complete_profile_heading: 'Complete your profile',
            step3_desc: 'Almost there! Tell us your name and choose your privacy settings.',
            first_name_label: 'First Name',
            name_placeholder: 'e.g. Marie',
            setting_up: 'Setting up…',
            go_dashboard_btn: 'Go to Dashboard',
          },
          register_student: {
            tagline: 'University student account',
            create_your_account_heading: 'Create your account',
            country_label: 'Country of study',
            authority_label: 'Education authority',
            year_label: 'Year of study',
            consent_curriculum_label: 'Allow curriculum data enrichment (national programme)',
          },
          register_university: {
            title: 'University Sign Up',
            step1_desc: 'Create your independent study account.',
            email_placeholder: 'you@university.fr',
            verify_login_btn: 'Verify & Log In',
            almost_done_heading: 'Almost done!',
            step3_desc: 'Help Claude understand your academic context.',
            name_placeholder: 'e.g. Alex',
            select_country_placeholder: 'Select your country…',
            select_year_placeholder: 'Select your year…',
          },
          general_info: {
            title: 'General Info',
            user_guide: 'User Guide',
            website: 'Visit our website',
          },
          dashboard: {
            inactive_days: '⚠️ Inactive › {{days}} days',
            view_summary: 'View summary →',
            view_full_details: 'View full details →',
            family_title: "{{name}}'s family",
            my_children_title: 'My children',
            subtitle: "Manage your children's study plans",
            add_child_btn: '+ Add child',
            link_student_btn: '🔗 Link a student',
            load_failed: 'Failed to load children. Please refresh.',
            empty_title: 'No children yet',
            empty_desc: 'Add a child to generate their join code and get started.',
            add_first_child_btn: 'Add your first child',
          },
        },
      },
      fr: {
        translation: {
          tabs: { home: 'Accueil', progress: 'Progrès', exams: 'Examens', timetable: 'Emploi du temps', more: 'Plus' },
          settings: { title: "Paramètres de l'application", language: 'Langue', app_language: "Langue de l'application" },
          actions: {
            signout: 'Se déconnecter', copy: 'Copier le code', previous: 'Précédent', next: 'Suivant',
            cancel: 'Annuler', saving: 'Enregistrement…', generating: 'Génération…', copied: '✓ Copié !',
            delete: 'Supprimer', remove: 'Retirer', filter_authorities: 'Filtrer les autorités…',
            loading: 'Chargement…', creating: 'Création…',
          },
          parent_dash: {
            title: 'Aperçu de la famille',
            empty: "Pas d'examens à venir ni de propositions actives",
            add_hs: 'Ajouter un étudiant',
            link_student: 'Associer un élève',
            link_account: 'Associer un compte',
            invite_code: "Code d'invitation",
            regen_code: "Regénérer le code d'accès",
            regen_new_code: 'Nouveau code',
            regen_failed: 'Échec de la régénération. Réessaie.',
            next_exam: 'Prochain examen',
            ai_proposal: 'Proposition IA',
            student_fallback: 'Élève',
            no_students_linked: "Tu n'as encore associé aucun profil d'élève. Ajoute un lycéen ou associe un compte existant ci-dessous.",
            link_success: 'Élève associé avec succès !',
            link_err_expired: "Ce code d'invitation a expiré. Demande à ton élève d'en générer un nouveau.",
            link_err_invalid: "Code d'invitation invalide. Vérifie et réessaie.",
            link_err_generic: "Une erreur s'est produite. Réessaie.",
          },
          parent_student: {
            profile_title: "Profil de l'élève",
            country: 'Pays',
            authority: 'Autorité',
            progress: 'Progrès scolaires',
            flashcards: 'Cartes mémos retournées',
            quiz_avg: 'Moyenne des quiz',
            topics: 'Sujets explorés',
            homework_completed: 'Terminés',
            weekly: 'Étude hebdomadaire',
            weekly_schedule: 'Emploi du temps hebdomadaire',
            today_proposals: "Propositions d'étude du jour",
            age_label: '{{age}} ans',
            schedule_desc: "Voir l'emploi du temps complet des cours et les activités extrascolaires.",
            view_schedule: "Voir l'emploi du temps complet",
            settings_profile: 'Paramètres & Profil',
            role_parent: 'Parent',
            lang: 'Langue',
            sign_out: 'Se déconnecter',
            edit_profile: 'Modifier le profil',
            select_country: 'Sélectionner le pays',
            no_authority_match: 'Aucune autorité ne correspond à ta recherche.',
            no_topics: 'Aucun sujet exploré pour le moment.',
            no_proposals: "Pas encore de propositions. Assure-toi que ton enfant a configuré son emploi du temps et ses niveaux de confiance.",
            slot_label: 'Créneau : {{slot}}',
            no_homework: 'Aucun devoir ajouté pour le moment.',
            open_label: 'En cours',
            homework_aria: 'Devoir {{n}}',
            save: '💾 Enregistrer',
            no_schedule_entries: "Aucun cours dans l'emploi du temps pour le moment.",
            classes_label: 'Cours',
            free_time_disclaimer: "Déclaré manuellement par l'élève — à titre indicatif uniquement, non utilisé pour planifier les propositions d'étude de l'IA.",
            update_schedule: "Mettre à jour l'emploi du temps",
            upload_schedule: "Importer l'emploi du temps",
          },
          academic_context: {
            study_level: "Niveau d'études",
            select_study_level: "Sélectionner le niveau d'études",
            confirm_title: "Réinitialiser l'année scolaire ?",
            confirm_body: "Changer ta classe ou ton pays effacera ton emploi du temps actuel, ton temps libre et tes engagements. Tu devras importer ton nouvel emploi du temps immédiatement. Continuer ?",
            confirm_continue: 'Continuer',
            banner_title: 'Nouvelle année scolaire définie — importe ton emploi du temps',
            banner_desc: "Ton emploi du temps, ton temps libre et tes engagements ont été effacés pour la nouvelle année scolaire. Importe ton nouvel emploi du temps pour retrouver des propositions d'étude.",
          },
          parent_subscription: {
            title: 'Abonnement',
            no_children: 'Ajoute un enfant pour voir les tarifs qui le concernent.',
            add_child_cta: 'Ajouter un enfant',
            select_child: 'Enfant',
          },
          student_subscription: {
            title: 'Abonnement',
          },
          subscription: {
            tier_accessible: 'Accessible',
            tier_standard: 'Standard',
            tier_premium: 'Premium',
            monthly: 'Mensuel',
            yearly: 'Annuel (paiement unique)',
            per_month: '/mois',
            per_year: '/an',
            per_child_note: 'Le prix affiché est par enfant.',
            sessions: '{{count}} sessions/mois',
            unlimited_sessions: 'Sessions illimitées',
            loading: 'Chargement des tarifs…',
            error: 'Impossible de charger les tarifs. Réessaie plus tard.',
          },
          parent_child_detail: {
            child_fallback: 'Enfant',
            university_note: "Les étudiants universitaires gèrent leurs propres données d'étude. Tu ne peux voir que les totaux hebdomadaires et la série.",
            tab_overview: "Vue d'ensemble",
            tab_timetable: 'Emploi du temps',
            tab_free_time: 'Temps libre',
            tab_confidence: 'Confiance',
            tab_study_plan: "Plan d'étude",
            tab_progress: 'Progrès',
          },
          overview_tab: {
            signin_code_title: 'Code de connexion de l’élève',
            share_code_desc: 'Partage ce code à 8 chiffres avec ton enfant — valide jusqu’au {{date}}.',
            copy: 'Copier',
            generate_new_code: 'Générer un nouveau code',
            generate_code_desc: 'Génère un code à 8 chiffres pour que ton enfant se connecte pour la première fois.',
            generate_code: 'Générer un code',
            ai_suggestion_title: "Suggestion d'étude IA",
            generating_suggestion: 'Génération de la suggestion…',
            suggestion_failed: "Impossible de générer une suggestion. Vérifie que l'emploi du temps et la confiance sont configurés.",
            generated_at: 'Généré {{time}}',
            loading_proposals: 'Chargement des propositions…',
            no_proposals: "Pas encore de propositions. Configure d'abord les niveaux de confiance et les créneaux libres.",
          },
          timetable_tab: {
            class_count: '{{count}} cours',
            grid_view: 'Grille',
            edit_view: 'Modifier',
            subject_label: 'Matière',
            day_label: 'Jour',
            start_time_label: 'Heure de début',
            end_time_label: 'Heure de fin',
            room_label: 'Salle (optionnel)',
            remove: 'Supprimer',
            add_class: '+ Ajouter un cours',
            save_failed: "Échec de l'enregistrement de l'emploi du temps",
            saved: 'Emploi du temps enregistré.',
            save_timetable: "Enregistrer l'emploi du temps",
            free_time_heading: 'Temps libre',
          },
          free_time_tab: {
            intro_desc: "Ceci est ta propre liste de référence de créneaux libres supplémentaires — elle n'est pas utilisée pour générer les propositions d'étude de l'IA. Les propositions d'étude sont planifiées automatiquement autour de l'emploi du temps et des activités.",
            label_label: 'Libellé (optionnel)',
            label_placeholder: 'p. ex. Après les cours',
            save_failed: "Échec de l'enregistrement du temps libre",
            add_slot: '+ Ajouter un créneau',
            saved: 'Temps libre enregistré.',
            save_free_time: 'Enregistrer le temps libre',
          },
          confidence_tab: {
            intro_desc: "Évalue la confiance pour chaque matière. L'IA s'en sert pour prioriser les propositions d'étude.",
            no_subjects: 'Pas encore de matières. Ajoute des cours à l\'emploi du temps ou saisis une matière ci-dessous.',
            add_subject_placeholder: 'Ajouter une matière manuellement…',
            add_btn: 'Ajouter',
            save_failed: "Échec de l'enregistrement des niveaux de confiance",
            saved: 'Confiance enregistrée.',
            save_btn: 'Enregistrer les niveaux de confiance',
          },
          progress_tab: {
            title: "Sessions d'étude",
            cancel: 'Annuler',
            log_session: '+ Enregistrer une session',
            log_session_title: "Enregistrer une session d'étude",
            subject_label: 'Matière',
            select_subject: 'Choisir une matière…',
            subject_placeholder: 'p. ex. Maths',
            topic_label: 'Sujet',
            topic_placeholder: 'p. ex. Équations du second degré',
            format_label: 'Format',
            format_short: 'Court (15 min)',
            format_standard: 'Standard (30 min)',
            format_deep: 'Approfondi (60 min)',
            duration_label: 'Durée (minutes)',
            note_label: 'Note (optionnel)',
            note_placeholder: "Qu'as-tu abordé ?",
            log_btn: 'Enregistrer la session',
            logged: 'Session enregistrée !',
            no_sessions: 'Aucune session enregistrée pour le moment.',
            log_first: 'Enregistre ta première session',
            log_failed: "Échec de l'enregistrement de la session. Réessaie.",
          },
          proposals_tab: {
            confidence_heading: 'Niveaux de confiance par matière',
            no_ratings: "Pas encore d'évaluations. Demande à ton enfant d'évaluer ses matières depuis sa page d'accueil.",
            plan_heading: "Plan d'étude du jour",
            plan_desc: "Généré à partir de l'emploi du temps, des niveaux de confiance et de l'historique d'étude de ton enfant.",
            no_proposals: "Pas encore de propositions.",
            no_proposals_desc: "Les propositions sont générées une fois que ton enfant a configuré son emploi du temps.",
            rated_label: 'Évalué :',
            suggested_slot: 'Créneau suggéré : {{slot}}',
          },
          child_context_edit: {
            authority_required: "Sélectionne au moins une autorité d'enseignement.",
            update_failed: 'Échec de la mise à jour',
            back: 'Retour',
            title: "Modifier le contexte scolaire",
            desc: 'Mets à jour le pays, l\'autorité et la classe de {{name}}.',
            desc_fallback: 'ton enfant',
            country_label: "Pays d'études",
            authorities_label: "Autorités d'enseignement",
            select_all_hint: '(sélectionne toutes celles qui s\'appliquent)',
            class_year_label: 'Classe / Année',
            save_changes: 'Enregistrer les modifications',
          },
          child_context_card: {
            title: 'Contexte scolaire',
            edit_link: 'Modifier →',
            curriculum_label: 'Programme',
            official_loaded: '✓ Données officielles chargées',
            data_pending: 'Données en attente',
            official_subjects_prefix: 'Matières officielles : {{subjects}}',
            more_suffix: '+{{count}} autres',
          },
          uni_more: {
            greeting: 'Salut, {{name}}',
            student_fallback: 'Élève',
            ai_tools_label: 'Outils IA',
            flashcards: 'Cartes mémos',
            my_schedule_label: 'Mon emploi du temps',
            my_timetable: 'Mon emploi du temps',
            commitments: 'Engagements',
            invite_parent_title: 'Inviter un parent',
            invite_parent_desc: "Donne à un parent un accès résumé à ta série d'étude. Il ne verra pas tes notes.",
          },
          scan_timetable: {
            title: "Scanner l'emploi du temps",
            read_error: "Impossible de lire l'image. Réessaie.",
            ocr_failed: "Claude n'a pas pu lire la photo. Essaie une image plus nette, ou saisis ton emploi du temps manuellement.",
            scanning_title: 'Claude lit ton emploi du temps…',
            scanning_desc: 'Cela prend généralement quelques secondes.',
            scan_camera_title: "Scanner l'emploi du temps papier",
            scan_camera_desc: 'Pointe ta caméra vers ton emploi du temps papier — Claude le lit automatiquement.',
            upload_title: 'Envoyer une photo',
            upload_desc: 'Choisis une photo existante depuis ta galerie ou tes fichiers.',
            or_divider: '— ou —',
            enter_manually: '✍️ Saisir manuellement',
          },
          notification_bell: {
            disable: 'Désactiver les rappels d\'étude',
            blocked: 'Notifications bloquées dans les paramètres du navigateur',
            enable: "Activer les rappels d'étude quotidiens",
          },
          streak_pill: {
            aria: 'série de {{count}} jours',
            days_one: '{{count}} jour',
            days_other: '{{count}} jours',
          },
          countdown_chip: {
            today: "Aujourd'hui !",
            days_left_one: 'dans {{count}} jour',
            days_left_other: 'dans {{count}} jours',
            with_label: '{{label}} : {{text}}',
          },
          weekly_grid: {
            legend_school: 'École',
            legend_activity: 'Activité',
            legend_free: 'Temps libre',
            time_col: 'Heure',
            aria_label: 'Emploi du temps hebdomadaire',
            free_fallback: 'Libre',
          },
          error_boundary: {
            title: "Une erreur s'est produite",
            desc: "Une erreur inattendue s'est produite. Nous en avons été informés.",
            go_home: "Retour à l'accueil",
          },
          app_shell: {
            skip_to_content: 'Aller au contenu',
            greeting: 'Salut, {{name}} !',
          },
          not_found: {
            title: 'Page introuvable',
            desc: "Cette page n'existe pas ou a été déplacée.",
          },
          offline: {
            title: 'Tu es hors ligne',
            desc: "VADOStudy a besoin d'une connexion pour générer du contenu. Les pages en cache restent disponibles.",
            retry: 'Réessayer la connexion',
          },
          network_status: {
            offline_banner: 'Tu es hors ligne. Certaines fonctionnalités peuvent être indisponibles.',
          },
          install_prompt: {
            banner_text: 'Installez VADOStudy pour un accès rapide, même hors ligne.',
            install_btn: 'Installer',
            ios_install_btn: 'Comment installer',
            dismiss: 'Ignorer',
            ios_sheet_title: 'Installer VADOStudy',
            ios_step_1: "Appuyez sur l'icône de partage dans la barre d'outils de Safari.",
            ios_step_2: 'Faites défiler et sélectionnez « Sur l\'écran d\'accueil ».',
            ios_step_3: 'Appuyez sur « Ajouter » — VADOStudy apparaîtra sur votre écran d\'accueil.',
            ios_got_it: "J'ai compris",
          },
          link_student: {
            err_length: "Merci de saisir le code d'invitation complet à 8 caractères.",
            success: '{{name}} a été associé(e) à ton compte.',
            err_not_found: "Code d'invitation introuvable. Vérifie le code et réessaie.",
            err_generic: "Échec de l'association. Réessaie.",
            dashboard_btn: 'Tableau de bord',
            title: 'Associer un élève',
            desc: "Saisis le code d'invitation à 8 caractères que ton étudiant universitaire t'a partagé.",
            submit_btn: "Associer l'élève",
            hint_prefix: 'Demande à ton élève d\'aller dans',
            hint_path: 'Plus → Inviter un parent',
            hint_suffix: "dans l'application VADOStudy pour générer son code.",
          },
          parent_footer: { family: 'Famille', sub: 'Abonnement', more: 'Plus' },
          student_dash: {
            greeting: 'Bonjour {{name}}',
            up_next: 'À suivre',
            day_schedule: 'Emploi du temps du {{day}}',
            explain: 'Expliquer',
            homework: 'Devoirs',
            no_open_assignments: 'Aucun devoir en cours',
            cards_btn: 'Cartes',
            add_exam_cta: 'Ajouter vos prochaines dates de contrôle',
            degraded_notice: 'Suggestions de base affichées — IA temporairement indisponible',
            upcoming_exam: 'Examen à venir',
            no_timetable_title: "Pas encore d'emploi du temps !",
            no_timetable_desc: "Claude a besoin de connaître ton emploi du temps pour générer ton plan d'étude personnalisé.",
            setup_timetable: 'Configurer mon emploi du temps',
            load_failed: 'Échec du chargement des données.',
            no_proposals: "Pas encore de propositions d'étude. Évalue tes matières pour que Claude puisse prioriser ton plan.",
            rate_subjects: 'Évaluer mes matières',
            get_homework_help: "Obtenir de l'aide pour les devoirs",
            my_homework: 'Mes devoirs',
            no_classes_today: "Pas de cours prévu aujourd'hui ! 🎉",
            hw_done_count: '{{done}}/{{total}} fait(s)',
          },
          notifications: {
            study_reminders: 'Rappels d\'étude',
            daily_digest_desc: 'Résumé quotidien et alertes d\'inactivité',
          },
          student_progress: {
            title: 'Mes Progrès',
            recent_topics: 'Sujets récemment explorés',
            algo_title: "Mettre à jour l'algorithme d'IA",
            algo_desc: "Évaluez votre confiance dans vos cours pour que Claude génère de meilleurs plans d'étude",
            review_btn: 'Évaluer la confiance par matière',
            flashcards_flipped: 'Cartes mémos retournées',
            quiz_average: 'Moyenne des quiz',
            homework_completed_heading: 'Devoirs terminés',
            exercise_count: '{{done}} / {{total}} exercice(s)',
          },
          student_timetable: {
            title: 'Mon Emploi du temps',
            other: 'Autres activités',
            add_free: 'Ajouter du temps libre',
            edit: "Modifier l'emploi du temps",
            days: { Mon: 'Lun', Tue: 'Mar', Wed: 'Mer', Thu: 'Jeu', Fri: 'Ven', Sat: 'Sam', Sun: 'Dim' },
            days_full: {
              monday: 'Lundi', tuesday: 'Mardi', wednesday: 'Mercredi', thursday: 'Jeudi',
              friday: 'Vendredi', saturday: 'Samedi', sunday: 'Dimanche',
            },
            empty_title: "Pas encore d'emploi du temps",
            empty_desc: "Ajoute tes cours pour que Claude puisse construire ton plan d'étude personnalisé.",
            scan_btn: '📸 Scanner un emploi du temps papier',
            enter_manually: '✍️ Saisir manuellement',
            activities_heading: '⚽ Activités',
            edit_activities: '✏️ Modifier les activités',
            add_activities: '⚽ Ajouter des activités',
            free_time_heading: '⏳ Temps libre',
            free_block: 'Créneau libre',
            edit_free_time: '✏️ Modifier le temps libre',
            commitment_types: { sport: 'Sport', music: 'Musique', work: 'Travail', other: 'Autre' },
          },
          school_calendar: {
            view_button: '📅 Calendrier scolaire',
            title: 'Calendrier scolaire',
            trimesters_heading: 'Dates des trimestres',
            holidays_heading: 'Vacances',
            exams_heading: "Périodes d'examens nationaux",
            zone_label: 'Zone {{zone}}',
            nationwide: 'Tout le pays',
            loading: 'Chargement du calendrier…',
            pending: 'Récupération du calendrier le plus récent… cela peut prendre un instant.',
            empty: "Le calendrier scolaire de ta région n'est pas encore disponible. Réessaie plus tard.",
            error: "Impossible de charger le calendrier scolaire pour le moment. Réessaie plus tard.",
          },
          student_more: {
            settings_profile: 'Paramètres & Profil',
            country: 'Pays',
            authority: 'Autorité',
            study_again: 'Étudier à nouveau',
            subject: 'Matière',
            select_subject: 'Sélectionner une matière',
            theme: 'Sujet/Thème',
            explain_this: 'Expliquer cela',
            new_flashcards: 'Nouvelles cartes mémos',
            notif_parent: 'Les notifications de votre compte sont gérées par votre parent.',
            revision_plan: 'Plan de révision',
            revision_plan_desc: "Propositions d'étude de la semaine",
            default_name: 'Élève',
            edit_details: 'Modifier les détails',
            select_country: 'Sélectionne ton pays',
            save_changes: '💾 Enregistrer',
            subject_placeholder: 'ex. Mathématiques',
            theme_placeholder: 'ex. Équations du second degré, la Seconde Guerre mondiale…',
            family_privacy: 'Confidentialité familiale',
            invite_desc: "Génère un code sécurisé pour permettre à un parent de voir ta série d'étude.",
            generate_invite: "Générer un code d'invitation",
            valid_until: "Valide jusqu'au {{date}}",
            generate_new_code: 'Générer un nouveau code',
            push_reminders_desc: 'Recevoir des rappels quotidiens sur cet appareil.',
            disable_notifications: 'Désactiver les notifications',
            enable_notifications: 'Activer les notifications',
          },
          revision_plan: {
            empty_title: "Pas encore de propositions.",
            empty_desc: "Ajoutez votre emploi du temps et vos examens pour obtenir des suggestions personnalisées.",
            scheduled: 'Prévu : {{slot}}',
          },
          manage_commitments: {
            title: 'Autres activités',
            add_activity: 'Ajouter une activité',
            name_label: 'Nom *',
            name_placeholder: 'ex. Entraînement de football',
            type_label: 'Type',
            day_label: 'Jour',
            start_time_label: 'Heure de début',
            end_time_label: 'Heure de fin',
            save_activity: '➕ Enregistrer',
            current_activities: 'Activités actuelles',
            empty: 'Pas encore d\'activités. Ajoutes-en une ci-dessus.',
            delete_aria: 'Supprimer {{label}}',
            added_toast: 'Activité ajoutée !',
            add_failed_toast: "Échec de l'ajout de l'activité. Réessayez.",
            removed_toast: 'Activité supprimée.',
            delete_failed_toast: 'Échec de la suppression. Réessayez.',
          },
          manage_free_time: {
            title: 'Ajouter du temps libre',
            add_block: 'Ajouter un créneau',
            label_optional: 'Libellé (facultatif)',
            label_placeholder: 'ex. Après l\'école, Week-end matin',
            add_to_list: '➕ Ajouter à la liste',
            slots_heading: 'Créneaux libres ({{count}})',
            empty: 'Pas encore de créneaux ajoutés.',
            remove_slot_aria: 'Retirer le créneau',
            save_btn: '💾 Enregistrer',
            saved_toast: 'Temps libre enregistré !',
            save_failed_toast: 'Échec de l\'enregistrement. Veuillez réessayer.',
          },
          manage_timetable: {
            edit_title: "Modifier l'emploi du temps",
            preview_heading: 'Aperçu de l\'emploi du temps',
            scan_read_error: "Échec du scan — impossible de lire l'image. Réessayez ou ajoutez les cours manuellement.",
            scan_unclear_error: 'Échec du scan — photo peu claire ? Réessayez ou ajoutez les cours manuellement.',
            scan_success: 'Claude a détecté {{count}} cours. Vérifiez ci-dessous et enregistrez quand vous êtes prêt.',
            scanning: '⏳ Analyse par Claude…',
            scan_photo_btn: "📷 Scanner une photo de l'emploi du temps",
            or_manual: 'ou ajouter manuellement',
            add_class: 'Ajouter un cours',
            subject_label: 'Matière *',
            room_label: 'Salle (facultatif)',
            room_placeholder: 'ex. A12',
            classes_heading: 'Cours ({{count}})',
            empty: 'Pas encore de cours ajoutés.',
            remove_entry_aria: "Retirer l'entrée",
            save_btn: '💾 Enregistrer',
            saved_toast: "Emploi du temps enregistré !",
          },
          confidence: {
            title: 'Confiance par matière',
            desc: "Évaluez votre ressenti pour vos cours afin d'obtenir de meilleures propositions d'étude IA.",
            struggling: 'En difficulté',
            on_track: 'Dans les clous',
            confident: 'En confiance',
            no_subjects: "Aucune matière trouvée. L'emploi du temps est peut-être encore en cours de chargement.",
          },
          ai_session: {
            offline_title: 'Aucune connexion internet',
            offline_cta: "Connecte-toi à internet pour utiliser les outils d'IA",
            limit_reached: "Tu as atteint ta limite mensuelle de sessions IA.",
            back_to_dashboard: 'Retour au tableau de bord',
          },
          explain: {
            leave: '← Quitter la session',
            got_it: 'Compris ! (+1 Série)',
            deep_dive: 'Approfondir',
            simpler: 'Expliquer plus simplement',
            claude_typing: 'Claude est en train d\'écrire…',
            prompt_question: "Qu'est-ce que Claude doit t'expliquer ?",
            topic_theme: 'Sujet / Thème',
            topic_placeholder: 'ex. Théorème de Pythagore, Seconde Guerre mondiale, Photosynthèse…',
            ask_btn: '💬 Demander à Claude d\'expliquer',
            generate_failed: "Échec de l'obtention de l'explication",
            deep_dive_msg: 'Je veux approfondir ce sujet — plus de détails et d\'exemples s\'il te plaît.',
            simpler_msg: 'Peux-tu expliquer cela plus simplement ?',
          },
          quiz: {
            title: 'Défi Quiz',
            choose: 'Combien de questions souhaitez-vous tenter ?',
            questions: '{{count}} Questions',
            header_title: 'Quiz',
            generating: 'Claude compose tes questions de quiz personnalisées…',
            generate_failed: 'Échec de la génération du quiz',
            results_title: 'Résultats',
            complete_title: 'Quiz terminé !',
            your_score: 'Ton score',
            pct_correct: '{{pct}} % de bonnes réponses',
            result_excellent: 'Excellent travail !',
            result_good: 'Bon effort !',
            result_keep_practising: 'Continue à t\'entraîner !',
            try_another: 'Essayer un autre quiz',
            running_score: 'Score : {{score}} / {{total}}',
          },
          flashcard: {
            question_label: 'Question',
            tap_flip: 'Appuyer pour retourner',
            got_it: 'Compris',
            need_practice: 'À revoir',
            generate_failed: 'Échec de la génération des cartes mémos',
            generating_session: 'Claude génère ta session d\'étude personnalisée…',
            choose_subject: 'Choisir une matière…',
            theme_placeholder: 'ex. La Révolution française, Trigonométrie…',
            answer_label: 'Réponse',
            empty_state: 'Impossible de générer les cartes mémos. Veuillez réessayer.',
          },
          session_complete: {
            title: 'Session terminée !',
            streak: '{{count}} jour(s) de suite',
            streak_plus: '+1 à la série',
            great_work: 'Bravo pour {{subject}}. Continuez sur votre lancée !',
            momentum: 'Gardez votre élan !',
            subject: 'Matière',
            theme: 'Thème',
            start_quiz: 'Démarrer le quiz',
            explain_this: 'Expliquer cela',
            new_cards: 'Nouvelles cartes',
            back_dash: 'Retour au tableau de bord',
          },
          exams: {
            title: 'Examens',
            add_exam_btn: '+ Ajouter vos prochaines dates de contrôle',
            no_exams: 'Aucun examen pour l\'instant.',
            add_first: 'Ajouter votre premier examen',
            past_exams: 'Examens passés ({{count}})',
            remove: 'Supprimer',
            subject: 'Matière',
            exam_name: 'Nom de l\'examen',
            date: 'Date',
            select_subject: 'Choisir une matière…',
            add_error: 'Impossible d\'ajouter l\'examen. Veuillez réessayer.',
            sheet_title: 'Ajouter vos prochaines dates de contrôle',
            subject_placeholder: 'ex. Maths',
            name_placeholder: 'ex. Examen final',
            delete_aria: "Supprimer l'examen {{name}}",
          },
          exams_grid: {
            title: 'Examens à venir',
            free_label: 'Libre',
            editing_label: 'Modification : {{slot}}',
            close: 'Fermer',
          },
          add_child: {
            title: 'Ajouter un étudiant',
            first_name: 'Prénom',
            mode: 'Mode',
            high_school: 'Lycée',
            university: 'Université',
            year_of_study: "Année d'études",
            select_year: 'Choisir une année',
            date_of_birth: 'Date de naissance',
            gdpr_note: 'Obligatoire pour les élèves de moins de 16 ans (RGPD)',
            education_authority: "Autorité d'enseignement",
            search_authority: 'Rechercher une autorité…',
            selected: '{{count}} sélectionné(s)',
            track_helper: 'Choisis jusqu\'à {{count}}. Ces matières recevront plus de profondeur et de priorité dans les suggestions de l\'IA.',
            generate_code: 'Générer le code de connexion',
            name_placeholder: 'p. ex. Emma',
            authority_required: "Sélectionne au moins une autorité d'enseignement.",
            year_required: "Sélectionne une année d'études.",
            year_cleared_notice: "L'année d'études a été réinitialisée car elle n'est pas disponible pour ce pays — merci de la resélectionner.",
            dob_age_range: 'La date de naissance doit indiquer un âge entre 10 et 25 ans.',
            err_generic: 'Une erreur est survenue. Veuillez réessayer.',
            profile_created: 'Profil créé !',
            share_code_desc: 'Partage ce code avec {{name}}. Il ou elle doit le saisir sur son appareil pour associer son compte.',
            valid_until: "Valide jusqu'au {{date}}",
            valid_72h: 'Valide pendant 72 heures',
            shown_once: 'affiché une seule fois',
            done_btn: 'Terminé',
          },
          homework: {
            title: 'Devoirs',
            ocr_pending: '⏳ Extraction des exercices…',
            add_btn: '+ Ajouter un devoir',
            add_hw_title: 'Ajouter un devoir',
            subject: 'Matière',
            select_subject: 'Choisir une matière',
            topics: 'Sujets / Thème',
            add_theme: '+ Ajouter',
            image_label: 'Image du devoir',
            image_optional: "(optionnel — Claude extraira les exercices)",
            tap_upload: 'Appuyer pour télécharger une photo',
            create_btn: 'Créer le devoir',
            done: 'fait',
            exercise: 'Exercice',
            progress_label: 'Progression',
            ai_help: "Obtenir l'aide de l'IA pour cet exercice",
            chat_prompt: 'Posez une question sur cet exercice.',
            chat_guide: "L'IA vous guidera — sans vous donner la réponse.",
            chat_placeholder: 'Demandez un indice…',
            chat_send: 'Envoyer',
            empty: 'Pas encore de devoirs. Appuie sur « Ajouter un devoir » pour commencer.',
            open_section: 'En cours',
            completed_section: 'Terminés',
            done_badge: 'Terminé',
            delete_aria: 'Supprimer {{subject}}',
            item_aria: 'Devoir {{index}}',
            not_found: 'Devoir introuvable.',
            back_to_list: 'Retour à la liste des devoirs',
            view_document: '📄 Voir le document original',
            document_alt: 'Document du devoir',
            help_title: 'Aide aux devoirs',
            loading_exercise: "Chargement de l'exercice…",
            see_document: '📄 Voir le document',
            chat_error: "Impossible d'obtenir une réponse. Veuillez réessayer.",
            review_title: 'Vérifier les exercices',
            extracted_count: '{{count}} exercice(s) extrait(s) par Claude',
            start_working: '🚀 Commencer',
            other_option: 'Autre…',
            subject_placeholder: 'ex. Mathématiques',
            enter_subject_name: 'Entrer le nom de la matière',
            topic_placeholder: 'ex. Dérivation',
            scan_btn: '📷 Scanner',
            upload_btn: '📁 Importer',
            supported_formats: 'Formats pris en charge : JPEG, PNG, WebP, PDF.',
            subject_required: 'Veuillez sélectionner ou saisir une matière.',
            create_failed: 'Impossible de créer le devoir. Veuillez réessayer.',
            preview_alt: 'Aperçu',
          },
          auth: {
            forgot_password: 'Mot de passe oublié ?',
            forgot_title: 'Réinitialiser le mot de passe',
            forgot_desc: 'Entrez votre email et nous vous enverrons un code de réinitialisation.',
            send_code: 'Envoyer le code',
            check_email_title: 'Vérifiez votre boîte mail',
            check_email_desc: 'Nous avons envoyé un code à 6 chiffres à {{email}}. Entrez-le ci-dessous.',
            code_label: 'Code de vérification',
            new_password: 'Nouveau mot de passe',
            confirm_password: 'Confirmer le mot de passe',
            confirm_reset: 'Définir le nouveau mot de passe',
            passwords_mismatch: 'Les mots de passe ne correspondent pas',
            resend_code: 'Renvoyer le code',
            back_to_login: 'Retour à la connexion',
            success_banner: 'Mot de passe mis à jour — veuillez vous connecter.',
            err_not_found: 'Aucun compte trouvé pour cet email.',
            err_code_mismatch: 'Code invalide ou expiré. Veuillez réessayer.',
            err_weak_password: 'Le mot de passe doit contenir au moins 8 caractères.',
            child_welcome: 'Bienvenue, élève !',
            child_instructions: "Entre le code à 8 chiffres fourni par ton parent pour accéder à ton espace d'étude.",
            verifying: 'Vérification…',
            sign_in_btn: 'Se connecter',
            join_code_invalid: "Code invalide ou expiré. Demande à tes parents d'en générer un nouveau.",
            tagline: "Organisateur d'études propulsé par l'IA",
            language_label: 'Choisissez votre langue',
            sign_in_failed: 'Échec de la connexion. Veuillez réessayer.',
            email_label: 'E-mail',
            email_placeholder: 'vous@exemple.com',
            password_label: 'Mot de passe',
            signing_in: 'Connexion en cours…',
            no_account: 'Vous n\'avez pas de compte ?',
            create_one: 'Créez-en un',
            have_join_code: 'Élève avec un code de connexion ?',
            sign_in_here: 'Connectez-vous ici',
            university_prompt: 'Étudiant universitaire ?',
            create_account_btn: 'Créez votre compte',
          },
          register: {
            parent_tagline: 'Créez votre compte parent',
            create_account_heading: 'Créer un compte',
            password_hint: '12 caractères minimum · majuscule, minuscule, chiffre et symbole requis',
            continue_btn: 'Continuer',
            already_have_account: 'Vous avez déjà un compte ?',
            sign_in_link: 'Se connecter',
            verify_email_heading: 'Vérifiez votre e-mail',
            code_sent: 'Nous avons envoyé un code à 6 chiffres à',
            confirmation_code_label: 'Code de confirmation',
            verify_btn: 'Vérifier',
            your_profile_heading: 'Votre profil',
            first_name_label: 'Votre prénom',
            privacy_choices: 'Choix de confidentialité',
            consent_ai_label: "Autoriser les suggestions d'étude par IA (utilise l'API Claude)",
            consent_curriculum_label: "Autoriser l'enrichissement des données du programme (programme national français)",
            tos_prefix: 'En continuant, vous acceptez les',
            tos_terms: "Conditions d'utilisation de VADOStudy",
            tos_suffix: 'et confirmez que toutes les données sont traitées dans {{region}}.',
            region_eu_west_3: "l'UE (région de Paris)",
            region_af_south_1: "l'Afrique (région du Cap)",
            get_started_btn: 'Commencer',
            signup_failed: "Échec de l'inscription",
            confirm_failed: 'Échec de la confirmation',
            profile_failed: 'Échec de la création du profil',
          },
          register_parent: {
            err_password: 'Le mot de passe doit contenir au moins 8 caractères avec majuscule, minuscule, chiffre et symbole.',
            err_exists: 'Un compte avec cet e-mail existe déjà. Essayez de vous connecter.',
            err_code_mismatch: 'Code incorrect. Vérifiez votre e-mail et réessayez.',
            err_code_expired: 'Code expiré. Veuillez revenir en arrière et en demander un nouveau.',
            err_generic: 'Une erreur est survenue. Veuillez réessayer.',
            code_resent: 'Un nouveau code a été envoyé à votre e-mail.',
            resending: 'Envoi en cours…',
            title: 'Créer un compte parent',
            step1_desc: "Suivez la progression scolaire de votre enfant et ses plans d'étude IA.",
            email_address_label: 'Adresse e-mail',
            password_placeholder: 'Au moins 8 caractères',
            creating_account: 'Création du compte…',
            create_account_btn: 'Créer un compte',
            check_email_heading: 'Vérifiez votre e-mail',
            verifying: 'Vérification…',
            verify_continue_btn: 'Vérifier et continuer',
            complete_profile_heading: 'Complétez votre profil',
            step3_desc: 'Presque terminé ! Indiquez votre nom et choisissez vos paramètres de confidentialité.',
            first_name_label: 'Prénom',
            name_placeholder: 'ex. Marie',
            setting_up: 'Configuration…',
            go_dashboard_btn: 'Aller au tableau de bord',
          },
          register_student: {
            tagline: 'Compte étudiant universitaire',
            create_your_account_heading: 'Créez votre compte',
            country_label: "Pays d'études",
            authority_label: "Autorité d'enseignement",
            year_label: "Année d'études",
            consent_curriculum_label: "Autoriser l'enrichissement des données du programme (programme national)",
          },
          register_university: {
            title: 'Inscription universitaire',
            step1_desc: "Créez votre compte d'étude indépendant.",
            email_placeholder: 'vous@universite.fr',
            verify_login_btn: 'Vérifier et se connecter',
            almost_done_heading: 'Presque terminé !',
            step3_desc: 'Aidez Claude à comprendre votre contexte académique.',
            name_placeholder: 'ex. Alex',
            select_country_placeholder: 'Sélectionnez votre pays…',
            select_year_placeholder: 'Sélectionnez votre année…',
          },
          general_info: {
            title: 'Infos générales',
            user_guide: 'Guide utilisateur',
            website: 'Visiter notre site',
          },
          dashboard: {
            inactive_days: '⚠️ Inactif › {{days}} jours',
            view_summary: 'Voir le résumé →',
            view_full_details: 'Voir tous les détails →',
            family_title: 'Famille {{name}}',
            my_children_title: 'Mes enfants',
            subtitle: 'Gérez le planning de vos enfants',
            add_child_btn: '+ Ajouter un enfant',
            link_student_btn: '🔗 Associer un élève',
            load_failed: 'Échec du chargement des enfants. Veuillez actualiser.',
            empty_title: "Pas encore d'enfants",
            empty_desc: "Ajoutez un enfant pour générer son code de connexion et commencer.",
            add_first_child_btn: 'Ajouter votre premier enfant',
          },
        },
      },
      es: {
        translation: {
          tabs: { home: 'Inicio', progress: 'Progreso', exams: 'Exámenes', timetable: 'Horario', more: 'Más' },
          settings: { title: 'Configuración', language: 'Idioma', app_language: 'Idioma de la aplicación' },
          actions: {
            signout: 'Cerrar sesión', copy: 'Copiar código', previous: 'Anterior', next: 'Siguiente',
            cancel: 'Cancelar', saving: 'Guardando…', generating: 'Generando…', copied: '✓ ¡Copiado!',
            delete: 'Eliminar', remove: 'Quitar', filter_authorities: 'Filtrar autoridades…',
            loading: 'Cargando…', creating: 'Creando…',
          },
          parent_dash: {
            title: 'Resumen familiar',
            empty: 'No hay próximos exámenes ni propuestas activas',
            add_hs: 'Añadir un hijo',
            link_student: 'Vincular a un estudiante',
            link_account: 'Vincular una cuenta',
            invite_code: 'Código de invitación',
            regen_code: 'Regenerar código de acceso',
            regen_new_code: 'Nuevo código',
            regen_failed: 'Error al regenerar. Inténtalo de nuevo.',
            next_exam: 'Próximo examen',
            ai_proposal: 'Propuesta IA',
            student_fallback: 'Estudiante',
            no_students_linked: 'Aún no has vinculado ningún perfil de estudiante. Añade un estudiante de secundaria o vincula uno existente a continuación.',
            link_success: '¡Estudiante vinculado correctamente!',
            link_err_expired: 'Este código de invitación ha caducado. Pide a tu estudiante que genere uno nuevo.',
            link_err_invalid: 'Código de invitación no válido. Compruébalo e inténtalo de nuevo.',
            link_err_generic: 'Algo salió mal. Inténtalo de nuevo.',
          },
          parent_student: {
            profile_title: 'Perfil del estudiante',
            country: 'País',
            authority: 'Autoridad',
            progress: 'Progreso académico',
            flashcards: 'Tarjetas de memoria volteadas',
            quiz_avg: 'Promedio de quiz',
            topics: 'Temas explorados',
            homework_completed: 'Completados',
            weekly: 'Estudio semanal',
            weekly_schedule: 'Horario semanal',
            today_proposals: 'Propuestas de estudio de hoy',
            age_label: '{{age}} años',
            schedule_desc: 'Ver el horario completo de clases y actividades extracurriculares.',
            view_schedule: 'Ver horario completo',
            settings_profile: 'Configuración y Perfil',
            role_parent: 'Padre',
            lang: 'Idioma',
            sign_out: 'Cerrar sesión',
            edit_profile: 'Editar perfil',
            select_country: 'Selecciona el país',
            no_authority_match: 'Ninguna autoridad coincide con tu búsqueda.',
            no_topics: 'Aún no se han explorado temas.',
            no_proposals: 'Todavía no hay propuestas. Asegúrate de que tu hijo haya configurado su horario y sus niveles de confianza.',
            slot_label: 'Franja: {{slot}}',
            no_homework: 'Aún no se ha añadido ninguna tarea.',
            open_label: 'Abierto',
            homework_aria: 'Tarea {{n}}',
            save: '💾 Guardar',
            no_schedule_entries: 'Aún no hay clases en el horario.',
            classes_label: 'Clases',
            free_time_disclaimer: 'Declarado manualmente por el estudiante — solo como referencia, no se usa para programar las propuestas de estudio de la IA.',
            update_schedule: 'Actualizar horario',
            upload_schedule: 'Subir horario',
          },
          academic_context: {
            study_level: 'Nivel de estudios',
            select_study_level: 'Selecciona el nivel de estudios',
            confirm_title: '¿Reiniciar el año académico?',
            confirm_body: 'Cambiar tu curso o país borrará tu horario actual, tu tiempo libre y tus compromisos. Deberás subir tu nuevo horario de inmediato. ¿Continuar?',
            confirm_continue: 'Continuar',
            banner_title: 'Nuevo año académico configurado — sube tu horario',
            banner_desc: 'Tu horario, tiempo libre y compromisos se borraron para el nuevo año académico. Sube tu nuevo horario para volver a recibir propuestas de estudio.',
          },
          parent_subscription: {
            title: 'Suscripción',
            no_children: 'Añade un hijo/a para ver los precios correspondientes.',
            add_child_cta: 'Añadir un hijo/a',
            select_child: 'Hijo/a',
          },
          student_subscription: {
            title: 'Suscripción',
          },
          subscription: {
            tier_accessible: 'Accessible',
            tier_standard: 'Standard',
            tier_premium: 'Premium',
            monthly: 'Mensual',
            yearly: 'Anual (pago único)',
            per_month: '/mes',
            per_year: '/año',
            per_child_note: 'El precio mostrado es por hijo/a.',
            sessions: '{{count}} sesiones/mes',
            unlimited_sessions: 'Sesiones ilimitadas',
            loading: 'Cargando precios…',
            error: 'No se pudieron cargar los precios. Inténtalo de nuevo.',
          },
          parent_child_detail: {
            child_fallback: 'Hijo/a',
            university_note: 'Los estudiantes universitarios gestionan sus propios datos de estudio. Solo puedes ver los totales semanales y la racha.',
            tab_overview: 'Resumen',
            tab_timetable: 'Horario',
            tab_free_time: 'Tiempo libre',
            tab_confidence: 'Confianza',
            tab_study_plan: 'Plan de estudio',
            tab_progress: 'Progreso',
          },
          overview_tab: {
            signin_code_title: 'Código de acceso del estudiante',
            share_code_desc: 'Comparte este código de 8 dígitos con tu hijo/a — válido hasta {{date}}.',
            copy: 'Copiar',
            generate_new_code: 'Generar nuevo código',
            generate_code_desc: 'Genera un código de 8 dígitos para que tu hijo/a inicie sesión por primera vez.',
            generate_code: 'Generar código',
            ai_suggestion_title: 'Sugerencia de estudio de la IA',
            generating_suggestion: 'Generando sugerencia…',
            suggestion_failed: 'No se pudo generar la sugerencia. Comprueba que el horario y la confianza estén configurados.',
            generated_at: 'Generado {{time}}',
            loading_proposals: 'Cargando propuestas…',
            no_proposals: 'Todavía no hay propuestas. Configura primero los niveles de confianza y las franjas libres.',
          },
          timetable_tab: {
            class_count: '{{count}} clases',
            grid_view: 'Cuadrícula',
            edit_view: 'Editar',
            subject_label: 'Asignatura',
            day_label: 'Día',
            start_time_label: 'Hora de inicio',
            end_time_label: 'Hora de fin',
            room_label: 'Aula (opcional)',
            remove: 'Eliminar',
            add_class: '+ Añadir clase',
            save_failed: 'Error al guardar el horario',
            saved: 'Horario guardado.',
            save_timetable: 'Guardar horario',
            free_time_heading: 'Tiempo libre',
          },
          free_time_tab: {
            intro_desc: 'Esta es tu propia lista de referencia de bloques libres adicionales — no se usa para generar propuestas de estudio de la IA. Las propuestas de estudio se programan automáticamente en torno a tu horario y actividades.',
            label_label: 'Etiqueta (opcional)',
            label_placeholder: 'p. ej. Después de clase',
            save_failed: 'Error al guardar el tiempo libre',
            add_slot: '+ Añadir franja',
            saved: 'Tiempo libre guardado.',
            save_free_time: 'Guardar tiempo libre',
          },
          confidence_tab: {
            intro_desc: 'Valora la confianza en cada asignatura. La IA usa esto para priorizar las propuestas de estudio.',
            no_subjects: 'Aún no hay asignaturas. Añade clases al horario o escribe una asignatura abajo.',
            add_subject_placeholder: 'Añadir una asignatura manualmente…',
            add_btn: 'Añadir',
            save_failed: 'Error al guardar los niveles de confianza',
            saved: 'Confianza guardada.',
            save_btn: 'Guardar niveles de confianza',
          },
          progress_tab: {
            title: 'Sesiones de estudio',
            cancel: 'Cancelar',
            log_session: '+ Registrar sesión',
            log_session_title: 'Registrar una sesión de estudio',
            subject_label: 'Asignatura',
            select_subject: 'Seleccionar asignatura…',
            subject_placeholder: 'p. ej. Matemáticas',
            topic_label: 'Tema',
            topic_placeholder: 'p. ej. Ecuaciones cuadráticas',
            format_label: 'Formato',
            format_short: 'Corto (15 min)',
            format_standard: 'Estándar (30 min)',
            format_deep: 'Profundo (60 min)',
            duration_label: 'Duración (minutos)',
            note_label: 'Nota (opcional)',
            note_placeholder: '¿Qué repasaste?',
            log_btn: 'Registrar sesión',
            logged: '¡Sesión registrada!',
            no_sessions: 'Aún no hay sesiones registradas.',
            log_first: 'Registra tu primera sesión',
            log_failed: 'Error al registrar la sesión. Inténtalo de nuevo.',
          },
          proposals_tab: {
            confidence_heading: 'Niveles de confianza por asignatura',
            no_ratings: 'Aún no hay valoraciones. Pide a tu hijo/a que valore sus asignaturas desde su página de inicio.',
            plan_heading: 'Plan de estudio de hoy',
            plan_desc: 'Generado a partir del horario, los niveles de confianza y el historial de estudio de tu hijo/a.',
            no_proposals: 'Aún no hay propuestas.',
            no_proposals_desc: 'Las propuestas se generan una vez que tu hijo/a haya configurado su horario.',
            rated_label: 'Valorado:',
            suggested_slot: 'Franja sugerida: {{slot}}',
          },
          child_context_edit: {
            authority_required: 'Selecciona al menos una autoridad educativa.',
            update_failed: 'Error al actualizar',
            back: 'Atrás',
            title: 'Editar contexto educativo',
            desc: 'Actualiza el país, la autoridad y la clase de {{name}}.',
            desc_fallback: 'tu hijo/a',
            country_label: 'País de estudio',
            authorities_label: 'Autoridades educativas',
            select_all_hint: '(selecciona todas las que correspondan)',
            class_year_label: 'Clase / Año',
            save_changes: 'Guardar cambios',
          },
          child_context_card: {
            title: 'Contexto educativo',
            edit_link: 'Editar →',
            curriculum_label: 'Currículo',
            official_loaded: '✓ Datos oficiales cargados',
            data_pending: 'Datos pendientes',
            official_subjects_prefix: 'Asignaturas oficiales: {{subjects}}',
            more_suffix: '+{{count}} más',
          },
          uni_more: {
            greeting: 'Hola, {{name}}',
            student_fallback: 'Estudiante',
            ai_tools_label: 'Herramientas IA',
            flashcards: 'Tarjetas de memoria',
            my_schedule_label: 'Mi horario',
            my_timetable: 'Mi horario',
            commitments: 'Compromisos',
            invite_parent_title: 'Invitar a un padre',
            invite_parent_desc: 'Concede a un padre acceso resumido a tu racha de estudio. No verá tus notas.',
          },
          scan_timetable: {
            title: 'Escanear horario',
            read_error: 'No se pudo leer la imagen. Inténtalo de nuevo.',
            ocr_failed: 'Claude no pudo leer la foto. Prueba con una imagen más clara o introduce tu horario manualmente.',
            scanning_title: 'Claude está leyendo tu horario…',
            scanning_desc: 'Esto suele tardar unos segundos.',
            scan_camera_title: 'Escanear horario en papel',
            scan_camera_desc: 'Apunta la cámara a tu horario en papel — Claude lo lee automáticamente.',
            upload_title: 'Subir una foto',
            upload_desc: 'Elige una foto existente de tu galería o archivos.',
            or_divider: '— o —',
            enter_manually: '✍️ Introducir manualmente',
          },
          notification_bell: {
            disable: 'Desactivar los recordatorios de estudio',
            blocked: 'Notificaciones bloqueadas en la configuración del navegador',
            enable: 'Activar recordatorios de estudio diarios',
          },
          streak_pill: {
            aria: 'racha de {{count}} días',
            days_one: '{{count}} día',
            days_other: '{{count}} días',
          },
          countdown_chip: {
            today: '¡Hoy!',
            days_left_one: 'queda {{count}} día',
            days_left_other: 'quedan {{count}} días',
            with_label: '{{label}}: {{text}}',
          },
          weekly_grid: {
            legend_school: 'Colegio',
            legend_activity: 'Actividad',
            legend_free: 'Tiempo libre',
            time_col: 'Hora',
            aria_label: 'Horario semanal',
            free_fallback: 'Libre',
          },
          error_boundary: {
            title: 'Algo salió mal',
            desc: 'Se produjo un error inesperado. Ya nos han avisado.',
            go_home: 'Ir al inicio',
          },
          app_shell: {
            skip_to_content: 'Saltar al contenido',
            greeting: '¡Hola, {{name}}!',
          },
          not_found: {
            title: 'Página no encontrada',
            desc: 'Esta página no existe o se movió.',
          },
          offline: {
            title: 'Estás sin conexión',
            desc: 'VADOStudy necesita conexión para generar contenido. Las páginas guardadas en caché siguen disponibles.',
            retry: 'Reintentar conexión',
          },
          network_status: {
            offline_banner: 'Estás sin conexión. Algunas funciones pueden no estar disponibles.',
          },
          install_prompt: {
            banner_text: 'Instala VADOStudy para un acceso rápido, incluso sin conexión.',
            install_btn: 'Instalar',
            ios_install_btn: 'Cómo instalar',
            dismiss: 'Descartar',
            ios_sheet_title: 'Instalar VADOStudy',
            ios_step_1: 'Toca el icono de compartir en la barra de Safari.',
            ios_step_2: 'Desplázate y selecciona "Añadir a pantalla de inicio".',
            ios_step_3: 'Toca "Añadir" — VADOStudy aparecerá en tu pantalla de inicio.',
            ios_got_it: 'Entendido',
          },
          link_student: {
            err_length: 'Introduce el código de invitación completo de 8 caracteres.',
            success: '{{name}} se ha vinculado a tu cuenta.',
            err_not_found: 'Código de invitación no encontrado. Comprueba el código e inténtalo de nuevo.',
            err_generic: 'No se pudo vincular al estudiante. Inténtalo de nuevo.',
            dashboard_btn: 'Panel',
            title: 'Vincular a un estudiante',
            desc: 'Introduce el código de invitación de 8 caracteres que te compartió tu estudiante universitario.',
            submit_btn: 'Vincular estudiante',
            hint_prefix: 'Pide a tu estudiante que vaya a',
            hint_path: 'Más → Invitar a un padre',
            hint_suffix: 'en la aplicación VADOStudy para generar su código.',
          },
          parent_footer: { family: 'Familia', sub: 'Suscripción', more: 'Más' },
          student_dash: {
            greeting: 'Hola {{name}}',
            up_next: 'Siguiente',
            day_schedule: 'Horario del {{day}}',
            explain: 'Explicar',
            homework: 'Deberes',
            no_open_assignments: 'Sin tareas abiertas',
            cards_btn: 'Tarjetas',
            add_exam_cta: 'Añadir tus próximas fechas de control',
            degraded_notice: 'Mostrando sugerencias básicas — IA no disponible temporalmente',
            upcoming_exam: 'Próximo examen',
            no_timetable_title: '¡Aún no hay horario!',
            no_timetable_desc: 'Claude necesita conocer tu horario de clases para generar tu plan de estudio personalizado.',
            setup_timetable: 'Configurar mi horario',
            load_failed: 'Error al cargar los datos.',
            no_proposals: 'Aún no hay propuestas de estudio. Califica tus materias para que Claude pueda priorizar tu plan.',
            rate_subjects: 'Calificar mis materias',
            get_homework_help: 'Obtener ayuda con los deberes',
            my_homework: 'Mis deberes',
            no_classes_today: '¡No hay clases programadas para hoy! 🎉',
            hw_done_count: '{{done}}/{{total}} hecho(s)',
          },
          notifications: {
            study_reminders: 'Recordatorios de estudio',
            daily_digest_desc: 'Resumen diario y alertas de inactividad',
          },
          student_progress: {
            title: 'Mi Progreso',
            recent_topics: 'Temas explorados recientemente',
            algo_title: 'Actualizar algoritmo de IA',
            algo_desc: 'Califica cómo te sientes en tus clases para que Claude genere mejores planes de estudio',
            review_btn: 'Revisar confianza por materia',
            flashcards_flipped: 'Tarjetas de memoria volteadas',
            quiz_average: 'Promedio de quiz',
            homework_completed_heading: 'Deberes completados',
            exercise_count: '{{done}} / {{total}} ejercicio(s)',
          },
          student_timetable: {
            title: 'Mi Horario',
            other: 'Otras actividades',
            add_free: 'Añadir tiempo libre',
            edit: 'Editar horario',
            days: { Mon: 'Lun', Tue: 'Mar', Wed: 'Mié', Thu: 'Jue', Fri: 'Vie', Sat: 'Sáb', Sun: 'Dom' },
            days_full: {
              monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves',
              friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo',
            },
            empty_title: 'Aún no hay horario',
            empty_desc: 'Añade tus clases para que Claude pueda crear tu plan de estudio personalizado.',
            scan_btn: '📸 Escanear horario en papel',
            enter_manually: '✍️ Introducir manualmente',
            activities_heading: '⚽ Actividades',
            edit_activities: '✏️ Editar actividades',
            add_activities: '⚽ Añadir actividades',
            free_time_heading: '⏳ Tiempo libre',
            free_block: 'Bloque libre',
            edit_free_time: '✏️ Editar tiempo libre',
            commitment_types: { sport: 'Deporte', music: 'Música', work: 'Trabajo', other: 'Otro' },
          },
          school_calendar: {
            view_button: '📅 Calendario escolar',
            title: 'Calendario escolar',
            trimesters_heading: 'Fechas de los trimestres',
            holidays_heading: 'Vacaciones',
            exams_heading: 'Períodos de exámenes nacionales',
            zone_label: 'Zona {{zone}}',
            nationwide: 'Todo el país',
            loading: 'Cargando calendario…',
            pending: 'Obteniendo el calendario más reciente… esto puede tardar un momento.',
            empty: 'El calendario escolar de tu región aún no está disponible. Inténtalo más tarde.',
            error: 'No se pudo cargar el calendario escolar en este momento. Inténtalo más tarde.',
          },
          student_more: {
            settings_profile: 'Configuración y Perfil',
            country: 'País',
            authority: 'Autoridad',
            study_again: 'Estudiar de nuevo',
            subject: 'Materia',
            select_subject: 'Seleccionar materia',
            theme: 'Tema',
            explain_this: 'Explicar esto',
            new_flashcards: 'Nuevas tarjetas',
            notif_parent: 'Las notificaciones de tu cuenta son gestionadas por tu tutor.',
            revision_plan: 'Plan de repaso',
            revision_plan_desc: 'Propuestas de estudio de la semana',
            default_name: 'Estudiante',
            edit_details: 'Editar detalles',
            select_country: 'Selecciona tu país',
            save_changes: '💾 Guardar cambios',
            subject_placeholder: 'ej. Matemáticas',
            theme_placeholder: 'ej. Ecuaciones cuadráticas, la Segunda Guerra Mundial…',
            family_privacy: 'Privacidad familiar',
            invite_desc: 'Genera un código seguro para que un tutor vea tu racha de estudio.',
            generate_invite: 'Generar código de invitación',
            valid_until: 'Válido hasta {{date}}',
            generate_new_code: 'Generar nuevo código',
            push_reminders_desc: 'Recibe recordatorios diarios en este dispositivo.',
            disable_notifications: 'Desactivar notificaciones',
            enable_notifications: 'Activar notificaciones',
          },
          revision_plan: {
            empty_title: 'Aún no hay propuestas.',
            empty_desc: 'Añade tu horario y tus exámenes para obtener sugerencias personalizadas.',
            scheduled: 'Programado: {{slot}}',
          },
          manage_commitments: {
            title: 'Otras actividades',
            add_activity: 'Añadir actividad',
            name_label: 'Nombre *',
            name_placeholder: 'ej. Entrenamiento de fútbol',
            type_label: 'Tipo',
            day_label: 'Día',
            start_time_label: 'Hora de inicio',
            end_time_label: 'Hora de fin',
            save_activity: '➕ Guardar actividad',
            current_activities: 'Actividades actuales',
            empty: 'Aún no hay actividades. Añade una arriba.',
            delete_aria: 'Eliminar {{label}}',
            added_toast: '¡Actividad añadida!',
            add_failed_toast: 'No se pudo añadir la actividad. Inténtalo de nuevo.',
            removed_toast: 'Actividad eliminada.',
            delete_failed_toast: 'No se pudo eliminar. Inténtalo de nuevo.',
          },
          manage_free_time: {
            title: 'Añadir tiempo libre',
            add_block: 'Añadir un bloque',
            label_optional: 'Etiqueta (opcional)',
            label_placeholder: 'ej. Después de clase, Fin de semana por la mañana',
            add_to_list: '➕ Añadir a la lista',
            slots_heading: 'Bloques de tiempo libre ({{count}})',
            empty: 'Aún no hay bloques añadidos.',
            remove_slot_aria: 'Quitar bloque',
            save_btn: '💾 Guardar tiempo libre',
            saved_toast: '¡Tiempo libre guardado!',
            save_failed_toast: 'Error al guardar. Inténtalo de nuevo.',
          },
          manage_timetable: {
            edit_title: 'Editar horario',
            preview_heading: 'Vista previa del horario',
            scan_read_error: 'Error de escaneo — no se pudo leer la imagen. Inténtalo de nuevo o añade las clases manualmente.',
            scan_unclear_error: 'Error de escaneo — ¿foto poco clara? Inténtalo de nuevo o añade las clases manualmente.',
            scan_success: 'Claude detectó {{count}} clase(s). Revisa a continuación y guarda cuando estés listo.',
            scanning: '⏳ Escaneando con Claude…',
            scan_photo_btn: '📷 Escanear foto del horario',
            or_manual: 'o añadir manualmente',
            add_class: 'Añadir una clase',
            subject_label: 'Materia *',
            room_label: 'Aula (opcional)',
            room_placeholder: 'ej. A12',
            classes_heading: 'Clases ({{count}})',
            empty: 'Aún no hay clases añadidas.',
            remove_entry_aria: 'Quitar entrada',
            save_btn: '💾 Guardar horario',
            saved_toast: '¡Horario guardado!',
          },
          confidence: {
            title: 'Confianza por materia',
            desc: 'Califica cómo te sientes en tus clases para obtener mejores propuestas de estudio de IA.',
            struggling: 'Con dificultades',
            on_track: 'En camino',
            confident: 'Seguro/a',
            no_subjects: 'No se encontraron materias. Tu horario aún puede estar cargando.',
          },
          ai_session: {
            offline_title: 'Sin conexión a internet',
            offline_cta: 'Conéctate a internet para usar las herramientas de IA',
            limit_reached: 'Has alcanzado tu límite mensual de sesiones de IA.',
            back_to_dashboard: 'Volver al tablero',
          },
          explain: {
            leave: '← Salir de la sesión',
            got_it: '¡Entendido! (+1 Racha)',
            deep_dive: 'Profundizar',
            simpler: 'Explicar más simple',
            claude_typing: 'Claude está escribiendo…',
            prompt_question: '¿Qué debería explicarte Claude?',
            topic_theme: 'Tema / Asunto',
            topic_placeholder: 'ej. Teorema de Pitágoras, Segunda Guerra Mundial, Fotosíntesis…',
            ask_btn: '💬 Pedir a Claude que explique',
            generate_failed: 'No se pudo obtener la explicación',
            deep_dive_msg: 'Quiero profundizar en esto — más detalles y ejemplos, por favor.',
            simpler_msg: '¿Puedes explicar esto de forma más simple?',
          },
          quiz: {
            title: 'Desafío Quiz',
            choose: '¿Cuántas preguntas quieres responder?',
            questions: '{{count}} Preguntas',
            header_title: 'Quiz',
            generating: 'Claude está componiendo tus preguntas de quiz personalizadas…',
            generate_failed: 'No se pudo generar el quiz',
            results_title: 'Resultados',
            complete_title: '¡Quiz completado!',
            your_score: 'Tu puntuación',
            pct_correct: '{{pct}}% correcto',
            result_excellent: '¡Excelente trabajo!',
            result_good: '¡Buen esfuerzo!',
            result_keep_practising: '¡Sigue practicando!',
            try_another: 'Probar otro quiz',
            running_score: 'Puntuación: {{score}} / {{total}}',
          },
          flashcard: {
            question_label: 'Pregunta',
            tap_flip: 'Toca para voltear',
            got_it: 'Entendido',
            need_practice: 'Necesito practicar',
            generate_failed: 'No se pudieron generar las tarjetas',
            generating_session: 'Claude está generando tu sesión de estudio personalizada…',
            choose_subject: 'Elegir una materia…',
            theme_placeholder: 'ej. La Revolución Francesa, Trigonometría…',
            answer_label: 'Respuesta',
            empty_state: 'No se pudieron generar las tarjetas. Inténtalo de nuevo.',
          },
          session_complete: {
            title: '¡Sesión completada!',
            streak: '{{count}} día(s) de racha',
            streak_plus: '+1 a la racha',
            great_work: '¡Buen trabajo con {{subject}}! ¡Sigue así!',
            momentum: '¡Mantén el ritmo!',
            subject: 'Materia',
            theme: 'Tema',
            start_quiz: 'Iniciar quiz',
            explain_this: 'Explicar esto',
            new_cards: 'Nuevas tarjetas',
            back_dash: 'Volver al tablero',
          },
          exams: {
            title: 'Exámenes',
            add_exam_btn: '+ Añadir tus próximas fechas de control',
            no_exams: 'Aún no hay exámenes.',
            add_first: 'Añadir tu primer examen',
            past_exams: 'Exámenes pasados ({{count}})',
            remove: 'Eliminar',
            subject: 'Materia',
            exam_name: 'Nombre del examen',
            date: 'Fecha',
            select_subject: 'Seleccionar materia…',
            add_error: 'No se pudo añadir el examen. Por favor, inténtalo de nuevo.',
            sheet_title: 'Añadir tus próximas fechas de control',
            subject_placeholder: 'ej. Matemáticas',
            name_placeholder: 'ej. Examen final',
            delete_aria: 'Eliminar examen {{name}}',
          },
          exams_grid: {
            title: 'Próximos exámenes',
            free_label: 'Libre',
            editing_label: 'Editando: {{slot}}',
            close: 'Cerrar',
          },
          add_child: {
            title: 'Añadir un estudiante',
            first_name: 'Nombre',
            mode: 'Modo',
            high_school: 'Instituto',
            university: 'Universidad',
            year_of_study: 'Año de estudios',
            select_year: 'Seleccionar un año',
            date_of_birth: 'Fecha de nacimiento',
            gdpr_note: 'Obligatorio para menores de 16 años (RGPD)',
            education_authority: 'Autoridad educativa',
            search_authority: 'Buscar autoridad…',
            selected: '{{count}} seleccionado(s)',
            track_helper: 'Elige hasta {{count}}. Estas materias recibirán más profundidad y prioridad en las sugerencias de la IA.',
            generate_code: 'Generar código de acceso',
            name_placeholder: 'p. ej. Emma',
            authority_required: 'Selecciona al menos una autoridad educativa.',
            year_required: 'Selecciona un año de estudios.',
            year_cleared_notice: 'El año de estudios se restableció porque no está disponible para este país — vuelve a seleccionarlo.',
            dob_age_range: 'La fecha de nacimiento debe indicar una edad entre 10 y 25 años.',
            err_generic: 'Algo salió mal. Inténtalo de nuevo.',
            profile_created: '¡Perfil creado!',
            share_code_desc: 'Comparte este código con {{name}}. Debe introducirlo en su dispositivo para vincular su cuenta.',
            valid_until: 'Válido hasta el {{date}}',
            valid_72h: 'Válido durante 72 horas',
            shown_once: 'se muestra solo una vez',
            done_btn: 'Listo',
          },
          homework: {
            title: 'Deberes',
            ocr_pending: '⏳ Extrayendo ejercicios…',
            add_btn: '+ Añadir una tarea',
            add_hw_title: 'Añadir una tarea',
            subject: 'Materia',
            select_subject: 'Seleccionar una materia',
            topics: 'Temas / Asunto',
            add_theme: '+ Añadir',
            image_label: 'Imagen de los deberes',
            image_optional: "(opcional — Claude extraerá los ejercicios)",
            tap_upload: 'Pulsar para subir una foto',
            create_btn: 'Crear la tarea',
            done: 'hecho',
            exercise: 'Ejercicio',
            progress_label: 'Progreso',
            ai_help: 'Obtener ayuda de IA con este ejercicio',
            chat_prompt: 'Haz una pregunta sobre este ejercicio.',
            chat_guide: 'La IA te guiará — sin darte la solución.',
            chat_placeholder: 'Pide una pista…',
            chat_send: 'Enviar',
            empty: 'Aún no hay tareas. Toca "Añadir tarea" para empezar.',
            open_section: 'En curso',
            completed_section: 'Completadas',
            done_badge: 'Hecho',
            delete_aria: 'Eliminar {{subject}}',
            item_aria: 'Tarea {{index}}',
            not_found: 'Tarea no encontrada.',
            back_to_list: 'Volver a la lista de tareas',
            view_document: '📄 Ver documento original',
            document_alt: 'Documento de la tarea',
            help_title: 'Ayuda con la tarea',
            loading_exercise: 'Cargando ejercicio…',
            see_document: '📄 Ver documento',
            chat_error: 'No se pudo obtener una respuesta. Inténtalo de nuevo.',
            review_title: 'Revisar ejercicios',
            extracted_count: '{{count}} ejercicio(s) extraído(s) por Claude',
            start_working: '🚀 Empezar',
            other_option: 'Otro…',
            subject_placeholder: 'ej. Matemáticas',
            enter_subject_name: 'Introduce el nombre de la materia',
            topic_placeholder: 'ej. Derivación',
            scan_btn: '📷 Escanear',
            upload_btn: '📁 Subir',
            supported_formats: 'Formatos admitidos: JPEG, PNG, WebP, PDF.',
            subject_required: 'Selecciona o introduce una materia.',
            create_failed: 'No se pudo crear la tarea. Inténtalo de nuevo.',
            preview_alt: 'Vista previa',
          },
          auth: {
            forgot_password: '¿Olvidaste tu contraseña?',
            forgot_title: 'Restablecer contraseña',
            forgot_desc: 'Introduce tu email y te enviaremos un código de restablecimiento.',
            send_code: 'Enviar código',
            check_email_title: 'Revisa tu correo',
            check_email_desc: 'Enviamos un código de 6 dígitos a {{email}}. Introdúcelo a continuación.',
            code_label: 'Código de verificación',
            new_password: 'Nueva contraseña',
            confirm_password: 'Confirmar contraseña',
            confirm_reset: 'Establecer nueva contraseña',
            passwords_mismatch: 'Las contraseñas no coinciden',
            resend_code: 'Reenviar código',
            back_to_login: 'Volver al inicio de sesión',
            success_banner: 'Contraseña actualizada — por favor inicia sesión.',
            err_not_found: 'No se encontró ninguna cuenta con este email.',
            err_code_mismatch: 'Código inválido o caducado. Por favor inténtalo de nuevo.',
            err_weak_password: 'La contraseña debe tener al menos 8 caracteres.',
            child_welcome: '¡Bienvenido, estudiante!',
            child_instructions: 'Introduce el código de 8 dígitos que te dio tu tutor para acceder a tu espacio de estudio.',
            verifying: 'Verificando...',
            sign_in_btn: 'Iniciar sesión',
            join_code_invalid: 'Código inválido o caducado. Pide a tu tutor que genere uno nuevo.',
            tagline: 'Organizador de estudio impulsado por IA',
            language_label: 'Elige tu idioma',
            sign_in_failed: 'Error al iniciar sesión. Inténtalo de nuevo.',
            email_label: 'Correo electrónico',
            email_placeholder: 'tu@ejemplo.com',
            password_label: 'Contraseña',
            signing_in: 'Iniciando sesión…',
            no_account: '¿Aún no tienes cuenta?',
            create_one: 'Crea una',
            have_join_code: '¿Estudiante con un código de acceso?',
            sign_in_here: 'Inicia sesión aquí',
            university_prompt: '¿Estudiante universitario?',
            create_account_btn: 'Crea tu cuenta',
          },
          register: {
            parent_tagline: 'Crea tu cuenta de tutor',
            create_account_heading: 'Crear cuenta',
            password_hint: 'Mínimo 12 caracteres · se requiere mayúscula, minúscula, número y símbolo',
            continue_btn: 'Continuar',
            already_have_account: '¿Ya tienes una cuenta?',
            sign_in_link: 'Inicia sesión',
            verify_email_heading: 'Verifica tu correo',
            code_sent: 'Enviamos un código de 6 dígitos a',
            confirmation_code_label: 'Código de confirmación',
            verify_btn: 'Verificar',
            your_profile_heading: 'Tu perfil',
            first_name_label: 'Tu nombre',
            privacy_choices: 'Opciones de privacidad',
            consent_ai_label: 'Permitir sugerencias de estudio con IA (usa la API de Claude)',
            consent_curriculum_label: 'Permitir el enriquecimiento con datos del plan de estudios (programa nacional francés)',
            tos_prefix: 'Al continuar aceptas los',
            tos_terms: 'Términos de Servicio de VADOStudy',
            tos_suffix: 'y confirmas que todos los datos se procesan en {{region}}.',
            region_eu_west_3: 'la UE (región de París)',
            region_af_south_1: 'África (región de Ciudad del Cabo)',
            get_started_btn: 'Comenzar',
            signup_failed: 'Error al registrarse',
            confirm_failed: 'Error de confirmación',
            profile_failed: 'Error al crear el perfil',
          },
          register_parent: {
            err_password: 'La contraseña debe tener al menos 8 caracteres e incluir mayúscula, minúscula, número y símbolo.',
            err_exists: 'Ya existe una cuenta con este correo. Intenta iniciar sesión.',
            err_code_mismatch: 'Código incorrecto. Revisa tu correo e inténtalo de nuevo.',
            err_code_expired: 'Código caducado. Vuelve atrás y solicita uno nuevo.',
            err_generic: 'Algo salió mal. Inténtalo de nuevo.',
            code_resent: 'Se ha enviado un nuevo código a tu correo.',
            resending: 'Reenviando…',
            title: 'Crear cuenta de tutor',
            step1_desc: 'Supervisa el progreso de aprendizaje de tu hijo/a y sus planes de estudio con IA.',
            email_address_label: 'Correo electrónico',
            password_placeholder: 'Al menos 8 caracteres',
            creating_account: 'Creando cuenta…',
            create_account_btn: 'Crear cuenta',
            check_email_heading: 'Revisa tu correo',
            verifying: 'Verificando…',
            verify_continue_btn: 'Verificar y continuar',
            complete_profile_heading: 'Completa tu perfil',
            step3_desc: 'Ya casi está. Indícanos tu nombre y elige tus opciones de privacidad.',
            first_name_label: 'Nombre',
            name_placeholder: 'ej. María',
            setting_up: 'Configurando…',
            go_dashboard_btn: 'Ir al tablero',
          },
          register_student: {
            tagline: 'Cuenta de estudiante universitario',
            create_your_account_heading: 'Crea tu cuenta',
            country_label: 'País de estudio',
            authority_label: 'Autoridad educativa',
            year_label: 'Año de estudios',
            consent_curriculum_label: 'Permitir el enriquecimiento con datos del plan de estudios (programa nacional)',
          },
          register_university: {
            title: 'Registro universitario',
            step1_desc: 'Crea tu cuenta de estudio independiente.',
            email_placeholder: 'tu@universidad.es',
            verify_login_btn: 'Verificar e iniciar sesión',
            almost_done_heading: '¡Casi listo!',
            step3_desc: 'Ayuda a Claude a entender tu contexto académico.',
            name_placeholder: 'ej. Alex',
            select_country_placeholder: 'Selecciona tu país…',
            select_year_placeholder: 'Selecciona tu año…',
          },
          general_info: {
            title: 'Información general',
            user_guide: 'Guía del usuario',
            website: 'Visitar nuestro sitio',
          },
          dashboard: {
            inactive_days: '⚠️ Inactivo › {{days}} días',
            view_summary: 'Ver resumen →',
            view_full_details: 'Ver todos los detalles →',
            family_title: 'Familia de {{name}}',
            my_children_title: 'Mis hijos',
            subtitle: 'Gestiona los planes de estudio de tus hijos',
            add_child_btn: '+ Añadir hijo/a',
            link_student_btn: '🔗 Vincular a un estudiante',
            load_failed: 'Error al cargar los hijos. Por favor, actualiza.',
            empty_title: 'Aún no hay hijos',
            empty_desc: 'Añade un hijo para generar su código de acceso y empezar.',
            add_first_child_btn: 'Añade tu primer hijo/a',
          },
        },
      },
    },
  });

export default i18n;
